import React, { useMemo, useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Table,
  Space,
  Tag,
  Typography,
  message,
  Drawer,
  Divider,
  Row,
  Col,
  DatePicker,
  Radio,
  Popconfirm,
  InputNumber,
  Tabs,
  Upload,
  Switch,
  Modal,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  SaveOutlined,
  DeleteOutlined,
  EditOutlined,
  InteractionOutlined,
  SettingOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const { Option } = Select;
const { Text } = Typography;
const { TabPane } = Tabs;

type ActivityStatus = 'open' | 'closed';

interface ActivityRecord {
  id: number;
  category: string;
  module: string;
  type: string;
  description?: string;
  sortOrder?: number;
  allowRepeat?: boolean;
  title: {
    local: string;
    en: string;
    zh: string;
  };
  jumpType: string;
  status: ActivityStatus;
  validity: string;
  creator: string;
  createTime: string;
  updater: string;
  updateTime: string;
  userScope: string;
  visibleTags?: number[]; // 可见标签ID数组，可选 - 哪些标签的用户可以看到活动
  rewardTags?: number[]; // 可领奖标签ID数组，可选 - 哪些标签的用户可以领取奖励
  config?: any;
  // 活动领取条件
  claimConditions?: {
    sameIpRewardCount?: number; // 同IP奖励数量：同一个IP，有多少个用户使用对应IP领取奖励
    sameDeviceRewardCount?: number; // 同设备奖励数量：同一个设备，有多少个用户使用对应设备领取奖励
    languageMessages?: { // 多语言提示，按语言分别配置 IP/设备限制文案
      vi?: {
        ipLimitMessage?: string;
        deviceLimitMessage?: string;
      };
      en?: {
        ipLimitMessage?: string;
        deviceLimitMessage?: string;
      };
      zh?: {
        ipLimitMessage?: string;
        deviceLimitMessage?: string;
      };
    } | undefined;
  };
}

interface FilterState {
  keyword?: string;
  status?: ActivityStatus | 'all';
  module?: string;
  type?: string;
  userScope?: string;
}

const STATUS_MAP: Record<ActivityStatus, { text: string; color: string }> = {
  open: { text: '开启', color: 'success' },
  closed: { text: '关闭', color: 'default' },
};

const MODULE_OPTIONS = ['代理', '主播招募', '轮盘', '活动大类'];
const TYPE_OPTIONS = ['直播赠金活动', '新手引导活动', '新手任务', '注册体验金活动', '主播活动', '新人推荐奖励', '尊享贵宾活动', '直播间在线时长奖励活动', '在线时长', '签到活动', '每日签到奖励', '下载APP奖励活动', '电子活动奖励活动'];
const USER_SCOPE_OPTIONS = ['全部', '代理', '主播', '新用户', '老用户'];

// 模拟标签数据（从用户标签管理获取）
const MOCK_TAGS = [
  { id: 1, name: 'VIP1', color: '#ff4d4f' },
  { id: 2, name: 'VIP2', color: '#ff7a45' },
  { id: 3, name: '活跃用户', color: '#52c41a' },
  { id: 4, name: '沉默用户', color: '#8c8c8c' },
  { id: 5, name: '高价值用户', color: '#1890ff' },
  { id: 6, name: '新用户', color: '#722ed1' },
];

// 厂商列表（从场馆游戏管理获取）
const VENUE_OPTIONS = [
  { value: 'JDB', label: 'JDB' },
  { value: 'PG', label: 'PG' },
  { value: 'CQ9', label: 'CQ9' },
  { value: 'PP', label: 'PP' },
  { value: 'JILI', label: 'JILI' },
  { value: 'EVO', label: 'EVO' },
];

// 模拟子游戏数据（实际应该从场馆游戏管理获取）
const MOCK_GAMES: Record<string, Array<{ code: string; name: string }>> = {
  'JDB': [
    { code: 'JDB-FISH-009', name: '影龙捕鱼' },
    { code: 'JDB-FISH-001', name: 'Super Dragon Fishing' },
    { code: 'JDB-SLOT-001', name: '幸运七' },
  ],
  'PG': [
    { code: 'PG-SLOT-777', name: 'Lucky 777' },
    { code: 'PG-SLOT-001', name: 'Wild Bounty' },
  ],
  'CQ9': [
    { code: 'CQ9-SLOT-001', name: '金鸡报喜' },
    { code: 'CQ9-FISH-001', name: '捕鱼达人' },
  ],
  'PP': [
    { code: 'PP-SLOT-001', name: 'Lucky Neko' },
    { code: 'PP-SLOT-002', name: 'Gates of Olympus' },
  ],
  'JILI': [
    { code: 'JILI-SLOT-001', name: 'Crazy Hunter' },
    { code: 'JILI-SLOT-002', name: 'Fortune Gems' },
  ],
  'EVO': [
    { code: 'EVO-LIVE-001', name: '经典百家乐' },
    { code: 'EVO-LIVE-002', name: '轮盘' },
  ],
};

const mockRecords: ActivityRecord[] = [
  {
    id: 1,
    category: '新人',
    module: '代理',
    type: '新手引导任务',
    description: '新用户首登引导任务',
    sortOrder: 1,
    allowRepeat: false,
    title: {
      local: '新手引导（越南语）',
      en: 'Guide Event',
      zh: '新手引导活动',
    },
    jumpType: '引导任务',
    status: 'open',
    validity: '永久有效',
    creator: 'admin',
    createTime: '2025-11-01 06:55:01',
    updater: 'admin',
    updateTime: '2025-11-14 03:07:01',
    userScope: '新用户',
    visibleTags: [6], // 新用户标签 - 可见
    rewardTags: [6], // 新用户标签 - 可领奖
  },
  {
    id: 2,
    category: '新人',
    module: '代理',
    type: '注册转盘奖励',
    description: '新用户注册转盘福利',
    sortOrder: 2,
    allowRepeat: true,
    title: {
      local: '注册转盘（越南语）',
      en: 'Register Wheel',
      zh: '注册转盘奖励',
    },
    jumpType: '轮盘',
    status: 'open',
    validity: '永久有效',
    creator: 'admin',
    createTime: '2025-11-01 06:58:38',
    updater: 'admin',
    updateTime: '2025-11-01 07:33:38',
    userScope: '新用户',
    visibleTags: [6], // 新用户标签 - 可见
    rewardTags: [6], // 新用户标签 - 可领奖
  },
  {
    id: 3,
    category: '代理',
    module: '代理',
    type: '老用户红包',
    description: '老用户召回红包雨',
    sortOrder: 3,
    allowRepeat: true,
    title: {
      local: '老用户红包（越南语）',
      en: 'Loyal Rain',
      zh: '老用户红包雨',
    },
    jumpType: '红包雨',
    status: 'open',
    validity: '永久有效',
    creator: 'admin',
    createTime: '2025-10-28 00:02:16',
    updater: 'admin',
    updateTime: '2025-10-28 17:07:52',
    userScope: '老用户',
    visibleTags: [1, 2], // VIP1和VIP2标签 - 可见
    rewardTags: [1, 2], // VIP1和VIP2标签 - 可领奖
  },
  {
    id: 4,
    category: '全部',
    module: '代理',
    type: '每日签到奖励',
    description: '签到连胜奖励',
    sortOrder: 4,
    allowRepeat: true,
    title: {
      local: '每日签到（越南语）',
      en: 'Daily Sign-in',
      zh: '每日签到奖励',
    },
    jumpType: '签到',
    status: 'open',
    validity: '永久有效',
    creator: 'admin',
    createTime: '2025-10-27 21:23:47',
    updater: 'admin',
    updateTime: '2025-10-27 21:23:47',
    userScope: '全部',
    visibleTags: [], // 不设置则全部可见
    rewardTags: [7], // 绑定银行卡标签 - 可领奖（示例：需要绑定银行卡才能领取，没有则提示需要绑定银行卡）
  },
  {
    id: 5,
    category: '全部',
    module: '代理',
    type: '在线时长',
    description: '直播间在线时长激励活动',
    sortOrder: 5,
    allowRepeat: true,
    title: {
      local: '在线时长奖励（越南语）',
      en: 'Online Duration Reward',
      zh: '在线时长奖励',
    },
    jumpType: '充值',
    status: 'open',
    validity: '永久有效',
    creator: 'admin',
    createTime: '2025-11-15 10:30:00',
    updater: 'admin',
    updateTime: '2025-11-15 10:30:00',
    userScope: '全部',
    visibleTags: [], // 不设置则全部可见
    rewardTags: [], // 不设置则全部可领奖
    claimConditions: {
      sameIpRewardCount: 3, // 同IP最多3个用户可领取
      sameDeviceRewardCount: 2, // 同设备最多2个用户可领取
      languageMessages: {
        vi: {
          ipLimitMessage: 'Địa chỉ IP này đã đạt giới hạn số lượng nhận thưởng, không thể tiếp tục nhận thưởng.',
          deviceLimitMessage: 'Thiết bị này đã đạt giới hạn số lượng nhận thưởng, không thể tiếp tục nhận thưởng.',
        },
        en: {
          ipLimitMessage: 'This IP address has reached the reward limit, cannot continue to claim rewards.',
          deviceLimitMessage: 'This device has reached the reward limit, cannot continue to claim rewards.',
        },
        zh: {
          ipLimitMessage: '该IP地址下的奖励领取数量已达上限，无法继续领取。',
          deviceLimitMessage: '该设备下的奖励领取数量已达上限，无法继续领取。',
        },
      },
    },
    config: {
      template: 'onlineDuration',
      onlineDuration: {
        durationMinutes: 30, // 在线时长30分钟
        turntableSpins: 1, // 转盘次数1次
        participationRatio: 2, // 参与比例2
        auditMultiplier: 2, // 彩金提现稽核倍数2
        rewardAmount: 50000, // 奖励金额50000 VND
        couponValue: 10000, // 优惠券金额10000 VND
        couponCount: 1, // 奖励多少优惠券1
        claimExpireMinutes: 5, // 手动领取时间5分钟
        usageExpireMinutes: 60, // 使用有效期60分钟
        showInList: true, // 展示在活动列表
        payoutMethod: 'auto', // 自动派发
        withdrawLimits: [
          { recharge: 100000, withdrawable: 100000 },
          { recharge: 500000, withdrawable: 500000 },
          { recharge: 1000000, withdrawable: 1000000 },
        ],
        egameCouponValue: 20000, // 电子游戏券金额20000 VND
        egameCouponCount: 1, // 电子游戏券数量1
        egameClaimExpireMinutes: 5,
        egameUsageExpireMinutes: 60,
        egameWithdrawLimits: [
          { recharge: 100000, withdrawable: 100000 },
          { recharge: 500000, withdrawable: 500000 },
          { recharge: 1000000, withdrawable: 1000000 },
        ],
      },
      onlineDurationRewardType: 'cash', // 奖励类型：真金
      langs: {
        vi: {
          title: '在线时长奖励（越南语）',
          jumpLink: '',
          richText: '观看直播间满30分钟即可获得奖励',
        },
        en: {
          title: 'Online Duration Reward',
          jumpLink: '',
          richText: 'Watch live room for 30 minutes to get rewards',
        },
        zh: {
          title: '在线时长奖励',
          jumpLink: '',
          richText: '观看直播间满30分钟即可获得奖励',
        },
      },
    },
  },
  {
    id: 6,
    category: '全部',
    module: '活动大类',
    type: '电子活动奖励活动',
    description: '电子厂商有效投注返利活动',
    sortOrder: 6,
    allowRepeat: true,
    title: {
      local: '电子活动奖励（越南语）',
      en: 'Egame Reward Activity',
      zh: '电子活动奖励活动',
    },
    jumpType: '电子返利',
    status: 'open',
    validity: '永久有效',
    creator: 'admin',
    createTime: '2026-01-31 09:00:00',
    updater: 'admin',
    updateTime: '2026-01-31 09:00:00',
    userScope: '全部',
    visibleTags: [],
    rewardTags: [],
    config: {
      template: 'egameReward',
      egameReward: {
        venues: ['PG', 'PP', 'JILI'],
        settleTime: '00:00',
        auditMultiplier: 1,
        maxReward: 68888888,
        ruleNote: '投注只限 PG、PP、JILI 三个场馆，返还金额仅需 1 倍打码即可出款。',
        rewardLadders: [
          { minBet: 1000000, reward: 18888 },
          { minBet: 3000000, reward: 38888 },
          { minBet: 10000000, reward: 188888 },
          { minBet: 30000000, reward: 388888 },
          { minBet: 100000000, reward: 1888888 },
          { minBet: 300000000, reward: 3888888 },
          { minBet: 1000000000, reward: 18888888 },
          { minBet: 3000000000, reward: 68888888 },
        ],
      },
      langs: {
        vi: {
          title: '电子活动奖励（越南语）',
          jumpLink: '',
          richText: '前一日有效投注达到档位即可获得返利。',
        },
        en: {
          title: 'Egame Reward Activity',
          jumpLink: '',
          richText: 'Daily effective bet settles to reward tiers.',
        },
        zh: {
          title: '电子活动奖励活动',
          jumpLink: '',
          richText: '前一日有效投注达到档位即可获得返利。',
        },
      },
    },
  },
];

type ActivityTemplateKey = 'live' | 'guide' | 'reward' | 'wheel' | 'redRain' | 'signIn' | 'onlineDuration' | 'downloadApp' | 'egameReward';

const ACTIVITY_TEMPLATES: Record<ActivityTemplateKey, { label: string; fields: Array<{ key: string; label: string; placeholder?: string; component?: 'number' | 'textarea' | 'datetime' }> }> = {
  live: {
    label: '直播活动配置',
    fields: [
      { key: 'liveRoom', label: '直播间编号', placeholder: '请输入直播间 ID' },
      { key: 'rewardPool', label: '奖励池金额(¥)', component: 'number' },
      { key: 'startTime', label: '开播时间', component: 'datetime' },
      { key: 'rules', label: '活动规则', component: 'textarea' },
    ],
  },
  guide: {
    label: '新手引导配置',
    fields: [],
  },
  reward: {
    label: '推荐奖励配置',
    fields: [
      { key: 'inviteRequirement', label: '邀请条件', component: 'textarea' },
      { key: 'rewardPerUser', label: '单人奖励(¥)', component: 'number' },
      { key: 'maxReward', label: '单日封顶(¥)', component: 'number' },
    ],
  },
  wheel: {
    label: '每日报名活动配置',
    fields: [],
  },
  redRain: {
    label: '红包雨配置',
    fields: [],
  },
  signIn: {
    label: '签到配置',
    fields: [],
  },
  onlineDuration: {
    label: '在线时长奖励配置',
    fields: [],
  },
  downloadApp: {
    label: '下载APP奖励配置',
    fields: [],
  },
  egameReward: {
    label: '电子活动奖励配置',
    fields: [],
  },
};

const GUIDE_TASKS = [
  { key: 'task1', title: '任务配置1', condition: '进入游戏直播间观看', unit: '分钟' },
  { key: 'task2', title: '任务配置2', condition: '首次参与直播间任务游戏', unit: '个任务' },
  { key: 'task3', title: '任务配置3', condition: '参与直播间游戏，并胜', unit: '次' },
  { key: 'task4', title: '任务配置4', condition: '成功充值任意金额', unit: '次' },
  { key: 'task5', title: '任务配置5', condition: '成功提现', unit: '次' },
  { key: 'task6', title: '任务配置6', condition: '首次邀请一名新用户', unit: '个' },
  { key: 'task7', title: '任务配置7', condition: '好友完成任意金额', unit: '次' },
];

const LANGUAGE_TABS = [
  { key: 'vi', label: '越南语' },
  { key: 'en', label: '英语' },
  { key: 'zh', label: '中文（繁体）' },
];

const DEFAULT_GUIDE_TASK_VALUES = GUIDE_TASKS.reduce<Record<string, { condition: string; dailyTimes: number; rewardAmount: number }>>((acc, task) => {
  acc[task.key] = {
    condition: task.condition,
    dailyTimes: 1,
    rewardAmount: 1000,
  };
  return acc;
}, {});

const DEFAULT_COUPON_TEMPLATE = {
  couponValue: 10000,
  withdrawLimits: [
    { recharge: 100000, withdrawable: 100000 },
    { recharge: 500000, withdrawable: 500000 },
    { recharge: 1000000, withdrawable: 1000000 },
  ],
  claimExpireMinutes: 5,
  usageExpireMinutes: 60,
};

const getDefaultCouponConfig = () => deepClone(DEFAULT_COUPON_TEMPLATE);

const WHEEL_REWARD_DEFAULTS = [
  { min: 1, max: 2, multiplier: 1.5 },
  { min: 3, max: 4, multiplier: 2.5 },
  { min: 5, max: 6, multiplier: 3.5 },
];

const RED_RAIN_LADDER_DEFAULTS = [
  { min: 1, max: 2, multiplier: 1.5 },
  { min: 3, max: 4, multiplier: 2.5 },
  { min: 5, max: 6, multiplier: 3.5 },
];

const DEFAULT_RED_RAIN_CONFIG = {
  rewardType: 'fixed',
  payoutMethod: 'auto',
  minReward: 20,
  maxReward: 100,
  burstCount: 5,
  multiplier: 3,
  rainDuration: 20,
  registerDays: 1,
  inactiveDays: 2,
  participationIntervalDays: 5,
  showInList: true,
  showOnHome: false,
  enableInflation: true,
  claimLimitMinutes: 2880,
  inflationMinutes: 2880,
  inflationMax: 300000,
  sameDeviceLimit: 99,
  sameIpLimit: 99,
};

const SIGN_IN_REWARD_DEFAULTS = [
  { day: 1, reward: 1000 },
  { day: 2, reward: 200 },
  { day: 3, reward: 300 },
  { day: 4, reward: 400 },
  { day: 5, reward: 500 },
  { day: 6, reward: 601 },
  { day: 7, reward: 701 },
];

const DEFAULT_EGAME_REWARD_LADDERS = [
  { minBet: 1000000, reward: 18888 },
  { minBet: 3000000, reward: 38888 },
  { minBet: 10000000, reward: 188888 },
  { minBet: 30000000, reward: 388888 },
  { minBet: 100000000, reward: 1888888 },
  { minBet: 300000000, reward: 3888888 },
  { minBet: 1000000000, reward: 18888888 },
  { minBet: 3000000000, reward: 68888888 },
];

const DEFAULT_EGAME_REWARD_CONFIG = {
  venues: ['PG', 'PP', 'JILI'],
  settleTime: '00:00',
  auditMultiplier: 1,
  maxReward: 68888888,
  ruleNote: '投注只限 PG、PP、JILI 三个场馆，返还金额仅需 1 倍打码即可出款。',
  rewardLadders: DEFAULT_EGAME_REWARD_LADDERS,
};

function deepClone<T>(data: T): T {
  return JSON.parse(JSON.stringify(data));
}

const inferTemplateKey = (type: string): ActivityTemplateKey => {
  if (type.includes('新手') || type.includes('引导')) return 'guide';
  if (type.includes('推荐')) return 'reward';
  if (type.includes('轮盘') || type.includes('转盘')) return 'wheel';
  if (type.includes('红包')) return 'redRain';
  if (type.includes('签到')) return 'signIn';
  if (type.includes('在线时长')) return 'onlineDuration';
  if (type.includes('下载APP') || type.includes('下载app')) return 'downloadApp';
  if (type.includes('电子')) return 'egameReward';
  return 'live';
};

// 活动大类选项（从活动大类管理获取，动态更新）
const getActivityCategoryOptions = (categories: ActivityCategory[]) => {
  return categories
    .filter(cat => cat.status === 'active')
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(cat => ({
      value: cat.name.zh,
      label: cat.name.zh,
    }));
};

// 活动大类接口
interface ActivityCategory {
  id: number;
  icon: string; // 保留旧字段用于兼容
  icons?: {
    dark: {
      selected: string; // 黑色皮肤选中icon URL
      unselected: string; // 黑色皮肤非选中icon URL
    };
    light: {
      selected: string; // 白色皮肤选中icon URL
      unselected: string; // 白色皮肤非选中icon URL
    };
  };
  name: {
    zh: string;
    zhTw: string;
    en: string;
    vi: string;
  };
  sortOrder: number;
  status: 'active' | 'inactive';
  createTime: string;
  updateTime: string;
}

// Demo活动大类数据
const mockActivityCategories: ActivityCategory[] = [
  {
    id: 1,
    icon: 'user',
    name: { zh: '新人', zhTw: '新人', en: 'New User', vi: 'Người mới' },
    sortOrder: 1,
    status: 'active',
    createTime: '2025-11-01 10:00:00',
    updateTime: '2025-11-01 10:00:00',
  },
  {
    id: 2,
    icon: 'team',
    name: { zh: '代理', zhTw: '代理', en: 'Agent', vi: 'Đại lý' },
    sortOrder: 2,
    status: 'active',
    createTime: '2025-11-01 10:00:00',
    updateTime: '2025-11-01 10:00:00',
  },
  {
    id: 3,
    icon: 'video-camera',
    name: { zh: '真人', zhTw: '真人', en: 'Live Dealer', vi: 'Người thật' },
    sortOrder: 3,
    status: 'active',
    createTime: '2025-11-01 10:00:00',
    updateTime: '2025-11-01 10:00:00',
  },
  {
    id: 4,
    icon: 'play-circle',
    name: { zh: '直播间游戏', zhTw: '直播間遊戲', en: 'Live Room Game', vi: 'Trò chơi phòng trực tiếp' },
    sortOrder: 4,
    status: 'active',
    createTime: '2025-11-01 10:00:00',
    updateTime: '2025-11-01 10:00:00',
  },
  {
    id: 5,
    icon: 'trophy',
    name: { zh: '体育', zhTw: '體育', en: 'Sports', vi: 'Thể thao' },
    sortOrder: 5,
    status: 'active',
    createTime: '2025-11-01 10:00:00',
    updateTime: '2025-11-01 10:00:00',
  },
  {
    id: 6,
    icon: 'appstore',
    name: { zh: '全部', zhTw: '全部', en: 'All', vi: 'Tất cả' },
    sortOrder: 0,
    status: 'active',
    createTime: '2025-11-01 10:00:00',
    updateTime: '2025-11-01 10:00:00',
  },
];

const LegacyActivityConfig: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm<FilterState>();
  const [editorForm] = Form.useForm<any>();
  const [records, setRecords] = useState<ActivityRecord[]>(mockRecords);
  const [filters, setFilters] = useState<FilterState>({ status: 'all' });
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<ActivityRecord | null>(null);
  const [templateKey, setTemplateKey] = useState<ActivityTemplateKey>('live');
  
  // 活动大类管理相关状态
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [categories, setCategories] = useState<ActivityCategory[]>(mockActivityCategories);
  const [categoryForm] = Form.useForm();
  const [editingCategory, setEditingCategory] = useState<ActivityCategory | null>(null);
  const [categoryEditModalVisible, setCategoryEditModalVisible] = useState(false);

  useEffect(() => {
    if (templateKey === 'guide') {
      if (!editorForm.getFieldValue('guideTasks')) {
        editorForm.setFieldsValue({
          guideTasks: DEFAULT_GUIDE_TASK_VALUES,
        });
      }
      if (!editorForm.getFieldValue('guideRewardType')) {
        editorForm.setFieldsValue({
          guideRewardType: 'cash',
        });
      }
      if (!editorForm.getFieldValue('guideCoupon')) {
        editorForm.setFieldsValue({
          guideCoupon: getDefaultCouponConfig(),
        });
      }
      if (!editorForm.getFieldValue(['guide', 'payoutMethod'])) {
        editorForm.setFieldsValue({
          guide: {
            ...(editorForm.getFieldValue('guide') || {}),
            payoutMethod: 'auto',
          },
        });
      }
    }
    if (templateKey === 'wheel') {
      if (!editorForm.getFieldValue('wheelRewards')) {
        editorForm.setFieldsValue({
          wheelRewards: WHEEL_REWARD_DEFAULTS,
        });
      }
      const wheelDefaults = editorForm.getFieldValue('wheel') || {
        rewardType: 'fixed',
        payoutMethod: 'auto',
        requireDeposit: false,
        showInList: true,
        showOnHome: false,
        enableInflation: true,
        unregisteredMinReward: 5000,
        unregisteredMaxReward: 10000,
      };
      editorForm.setFieldsValue({
        wheel: {
          rewardType: wheelDefaults.rewardType || 'fixed',
          payoutMethod: wheelDefaults.payoutMethod || 'auto',
          requireDeposit: wheelDefaults.requireDeposit ?? false,
          minRecharge: wheelDefaults.minRecharge || 5000,
          drawMin: wheelDefaults.drawMin || 5000,
          drawMax: wheelDefaults.drawMax || 50000,
          multiplier: wheelDefaults.multiplier || 3,
          joinUserType: wheelDefaults.joinUserType || 'registered',
          joinCycleDays: wheelDefaults.joinCycleDays || 5,
          joinTimesPerCycle: wheelDefaults.joinTimesPerCycle || 1,
          unregisteredMinReward: wheelDefaults.unregisteredMinReward || 5000,
          unregisteredMaxReward: wheelDefaults.unregisteredMaxReward || 10000,
          showInList: wheelDefaults.showInList ?? true,
          showOnHome: wheelDefaults.showOnHome ?? false,
          enableInflation: wheelDefaults.enableInflation ?? true,
          claimLimitMinutes: wheelDefaults.claimLimitMinutes || 1000,
          inflationMinutes: wheelDefaults.inflationMinutes || 500,
          inflationMax: wheelDefaults.inflationMax || 5000000,
          sameDeviceLimit: wheelDefaults.sameDeviceLimit || 99,
          sameIpLimit: wheelDefaults.sameIpLimit || 99,
        },
      });
      if (!editorForm.getFieldValue('wheelRewardType')) {
        editorForm.setFieldValue('wheelRewardType', 'cash');
      }
      if (!editorForm.getFieldValue('unregisteredRewardType')) {
        editorForm.setFieldValue('unregisteredRewardType', 'cash');
      }
      if (!editorForm.getFieldValue(['wheel', 'unregisteredCouponWithdrawLimits'])) {
        editorForm.setFieldValue(['wheel', 'unregisteredCouponWithdrawLimits'], [
          { recharge: 100000, withdrawable: 100000 },
          { recharge: 500000, withdrawable: 500000 },
          { recharge: 1000000, withdrawable: 1000000 },
        ]);
      }
      if (!editorForm.getFieldValue('wheelCoupon')) {
        editorForm.setFieldValue('wheelCoupon', getDefaultCouponConfig());
      }
    }
    if (templateKey === 'redRain') {
      if (!editorForm.getFieldValue('redRainRewards')) {
        editorForm.setFieldsValue({
          redRainRewards: RED_RAIN_LADDER_DEFAULTS,
        });
      }
      const rainDefaults = editorForm.getFieldValue('redRain') || DEFAULT_RED_RAIN_CONFIG;
      editorForm.setFieldsValue({
        redRain: {
          rewardType: rainDefaults.rewardType || 'fixed',
          payoutMethod: rainDefaults.payoutMethod || 'auto',
          minReward: rainDefaults.minReward || 20,
          maxReward: rainDefaults.maxReward || 100,
          burstCount: rainDefaults.burstCount || 5,
          multiplier: rainDefaults.multiplier || 3,
          rainDuration: rainDefaults.rainDuration || 20,
          registerDays: rainDefaults.registerDays || 1,
          inactiveDays: rainDefaults.inactiveDays || 2,
          participationIntervalDays: rainDefaults.participationIntervalDays || 5,
          showInList: rainDefaults.showInList ?? true,
          showOnHome: rainDefaults.showOnHome ?? false,
          enableInflation: rainDefaults.enableInflation ?? true,
          claimLimitMinutes: rainDefaults.claimLimitMinutes || 2880,
          inflationMinutes: rainDefaults.inflationMinutes || 2880,
          inflationMax: rainDefaults.inflationMax || 300000,
          sameDeviceLimit: rainDefaults.sameDeviceLimit || 99,
          sameIpLimit: rainDefaults.sameIpLimit || 99,
        },
      });
      if (!editorForm.getFieldValue('redRainRewardType')) {
        editorForm.setFieldValue('redRainRewardType', 'cash');
      }
      if (!editorForm.getFieldValue('redRainCoupon')) {
        editorForm.setFieldValue('redRainCoupon', getDefaultCouponConfig());
      }
    }
    if (templateKey === 'signIn') {
      if (!editorForm.getFieldValue('signInRewards')) {
        editorForm.setFieldsValue({
          signInRewards: SIGN_IN_REWARD_DEFAULTS,
        });
      }
      if (!editorForm.getFieldValue('signInRewardType')) {
        editorForm.setFieldValue('signInRewardType', 'cash');
      }
      if (!editorForm.getFieldValue('signInCoupon')) {
        editorForm.setFieldValue('signInCoupon', getDefaultCouponConfig());
      }
      if (!editorForm.getFieldValue(['signIn', 'payoutMethod'])) {
        editorForm.setFieldsValue({
          signIn: {
            ...(editorForm.getFieldValue('signIn') || {}),
            payoutMethod: 'auto',
          },
        });
      }
    }
    if (templateKey === 'onlineDuration') {
      if (!editorForm.getFieldValue('onlineDurationRewardType')) {
        editorForm.setFieldValue('onlineDurationRewardType', 'cash');
      }
      if (!editorForm.getFieldValue(['onlineDuration', 'payoutMethod'])) {
        editorForm.setFieldsValue({
          onlineDuration: {
            ...(editorForm.getFieldValue('onlineDuration') || {}),
            payoutMethod: 'auto',
          },
        });
      }
      if (!editorForm.getFieldValue(['onlineDuration', 'withdrawLimits'])) {
        editorForm.setFieldValue(['onlineDuration', 'withdrawLimits'], [
          { recharge: 100000, withdrawable: 100000 },
          { recharge: 500000, withdrawable: 500000 },
          { recharge: 1000000, withdrawable: 1000000 },
        ]);
      }
      if (!editorForm.getFieldValue(['onlineDuration', 'egameWithdrawLimits'])) {
        editorForm.setFieldValue(['onlineDuration', 'egameWithdrawLimits'], [
          { recharge: 100000, withdrawable: 100000 },
          { recharge: 500000, withdrawable: 500000 },
          { recharge: 1000000, withdrawable: 1000000 },
        ]);
      }
    }
    if (templateKey === 'downloadApp') {
      if (!editorForm.getFieldValue(['downloadApp', 'payoutMethod'])) {
        editorForm.setFieldsValue({
          downloadApp: {
            ...(editorForm.getFieldValue('downloadApp') || {}),
            payoutMethod: 'auto',
            enableApp: false,
            enablePwa: false,
          },
        });
      }
    }
    if (templateKey === 'egameReward') {
      const egameDefaults = editorForm.getFieldValue('egameReward') || {};
      editorForm.setFieldsValue({
        egameReward: {
          ...DEFAULT_EGAME_REWARD_CONFIG,
          ...egameDefaults,
          rewardLadders: egameDefaults.rewardLadders?.length
            ? egameDefaults.rewardLadders
            : DEFAULT_EGAME_REWARD_LADDERS,
        },
      });
    }
    editorForm.setFieldValue('template', templateKey);
  }, [templateKey, editorForm]);

  const handleSearch = () => {
    const values = form.getFieldsValue();
    setFilters(values);
  };

  const handleReset = () => {
    form.resetFields();
    setFilters({ status: 'all' });
  };

  const guideRewardType = Form.useWatch('guideRewardType', editorForm) || 'cash';
  const guidePayoutMethod = Form.useWatch(['guide', 'payoutMethod'], editorForm) || 'auto';
  const wheelRewardType = Form.useWatch('wheelRewardType', editorForm) || 'cash';
  const wheelPayoutMethod = Form.useWatch(['wheel', 'payoutMethod'], editorForm) || 'auto';
  const unregisteredRewardType = Form.useWatch('unregisteredRewardType', editorForm) || 'cash';
  const redRainRewardType = Form.useWatch('redRainRewardType', editorForm) || 'cash';
  const redRainPayoutMethod = Form.useWatch(['redRain', 'payoutMethod'], editorForm) || 'auto';
  const signInRewardType = Form.useWatch('signInRewardType', editorForm) || 'cash';
  const signInPayoutMethod = Form.useWatch(['signIn', 'payoutMethod'], editorForm) || 'auto';
  const onlineDurationRewardType = Form.useWatch('onlineDurationRewardType', editorForm) || 'cash';
  const onlineDurationPayoutMethod = Form.useWatch(['onlineDuration', 'payoutMethod'], editorForm) || 'auto';
  const downloadAppEnabled = Form.useWatch(['downloadApp', 'enableApp'], editorForm) || false;
  const downloadPwaEnabled = Form.useWatch(['downloadApp', 'enablePwa'], editorForm) || false;

  const filteredData = useMemo(() => {
    return records.filter(record => {
      if (filters.keyword) {
        const keyword = filters.keyword.toLowerCase();
        const titleMatch = Object.values(record.title).some(text => text.toLowerCase().includes(keyword));
        if (!titleMatch && !record.type.toLowerCase().includes(keyword)) {
          return false;
        }
      }
      if (filters.status && filters.status !== 'all' && record.status !== filters.status) {
        return false;
      }
      if (filters.module && record.module !== filters.module) {
        return false;
      }
      if (filters.type && record.type !== filters.type) {
        return false;
      }
      if (filters.userScope && record.userScope !== filters.userScope) {
        return false;
      }
      return true;
    });
  }, [records, filters]);

  const openDrawer = (record?: ActivityRecord) => {
    if (record) {
      setEditingRecord(record);
      editorForm.resetFields();
      const template = inferTemplateKey(record.type);
      setTemplateKey(template);
      editorForm.setFieldsValue({
        category: record.category,
        module: record.module,
        type: record.type,
        jumpType: record.jumpType,
        description: record.description,
        sortOrder: record.sortOrder,
        allowRepeat: record.allowRepeat ?? false,
        userScope: record.userScope,
        visibleTags: record.visibleTags || [],
        rewardTags: record.rewardTags || [],
        status: record.status,
        validity: record.validity === '永久有效' ? 'permanent' : 'custom',
        effectiveDate: record.validity === '永久有效' ? null : dayjs(record.validity),
        template,
        ...(record.config || {}),
        guideRewardType: record.config?.guideRewardType || 'cash',
        guideCoupon: record.config?.guideCoupon
          ? deepClone(record.config.guideCoupon)
          : getDefaultCouponConfig(),
        wheelRewardType: record.config?.wheelRewardType || 'cash',
        wheelCoupon: record.config?.wheelCoupon
          ? deepClone(record.config.wheelCoupon)
          : getDefaultCouponConfig(),
        unregisteredRewardType: record.config?.unregisteredRewardType || 'cash',
        redRainRewardType: record.config?.redRainRewardType || 'cash',
        redRainCoupon: record.config?.redRainCoupon
          ? deepClone(record.config.redRainCoupon)
          : getDefaultCouponConfig(),
        signInRewardType: record.config?.signInRewardType || 'cash',
        signInCoupon: record.config?.signInCoupon
          ? deepClone(record.config.signInCoupon)
          : getDefaultCouponConfig(),
        onlineDurationRewardType: record.config?.onlineDurationRewardType || 'cash',
        downloadApp: record.config?.downloadApp || {
          payoutMethod: 'auto',
          enableApp: false,
          enablePwa: false,
        },
        guideEgameCoupon: record.config?.guideEgameCoupon
          ? deepClone(record.config.guideEgameCoupon)
          : getDefaultCouponConfig(),
        wheelEgameCoupon: record.config?.wheelEgameCoupon
          ? deepClone(record.config.wheelEgameCoupon)
          : getDefaultCouponConfig(),
        redRainEgameCoupon: record.config?.redRainEgameCoupon
          ? deepClone(record.config.redRainEgameCoupon)
          : getDefaultCouponConfig(),
        signInEgameCoupon: record.config?.signInEgameCoupon
          ? deepClone(record.config.signInEgameCoupon)
          : getDefaultCouponConfig(),
        claimConditions: record.claimConditions ? {
          sameIpRewardCount: record.claimConditions.sameIpRewardCount,
          sameDeviceRewardCount: record.claimConditions.sameDeviceRewardCount,
          languageMessages: {
            vi: {
              ipLimitMessage: record.claimConditions.languageMessages?.vi?.ipLimitMessage || '',
              deviceLimitMessage: record.claimConditions.languageMessages?.vi?.deviceLimitMessage || '',
            },
            en: {
              ipLimitMessage: record.claimConditions.languageMessages?.en?.ipLimitMessage || '',
              deviceLimitMessage: record.claimConditions.languageMessages?.en?.deviceLimitMessage || '',
            },
            zh: {
              ipLimitMessage: record.claimConditions.languageMessages?.zh?.ipLimitMessage || '',
              deviceLimitMessage: record.claimConditions.languageMessages?.zh?.deviceLimitMessage || '',
            },
          },
        } : {
          sameIpRewardCount: undefined,
          sameDeviceRewardCount: undefined,
          languageMessages: {
            vi: { ipLimitMessage: '', deviceLimitMessage: '' },
            en: { ipLimitMessage: '', deviceLimitMessage: '' },
            zh: { ipLimitMessage: '', deviceLimitMessage: '' },
          },
        },
      });
    } else {
      setEditingRecord(null);
      setTemplateKey('live');
      editorForm.resetFields();
      editorForm.setFieldsValue({
        status: 'open',
        validity: 'permanent',
        template: 'live',
        userScope: '全部',
        visibleTags: [],
        rewardTags: [],
        description: '',
        sortOrder: records.length + 1,
        allowRepeat: false,
        guideTasks: deepClone(DEFAULT_GUIDE_TASK_VALUES),
        wheelRewards: deepClone(WHEEL_REWARD_DEFAULTS),
        redRainRewards: deepClone(RED_RAIN_LADDER_DEFAULTS),
        signInRewards: deepClone(SIGN_IN_REWARD_DEFAULTS),
        guideRewardType: 'cash',
        guideCoupon: getDefaultCouponConfig(),
        wheelRewardType: 'cash',
        wheelCoupon: getDefaultCouponConfig(),
        redRainRewardType: 'cash',
        redRainCoupon: getDefaultCouponConfig(),
        signInRewardType: 'cash',
        signInCoupon: getDefaultCouponConfig(),
        onlineDurationRewardType: 'cash',
        downloadApp: {
          payoutMethod: 'auto',
          enableApp: false,
          enablePwa: false,
        },
        guideEgameCoupon: getDefaultCouponConfig(),
        wheelEgameCoupon: getDefaultCouponConfig(),
        redRainEgameCoupon: getDefaultCouponConfig(),
        signInEgameCoupon: getDefaultCouponConfig(),
        egameReward: deepClone(DEFAULT_EGAME_REWARD_CONFIG),
        claimConditions: {
          sameIpRewardCount: undefined,
          sameDeviceRewardCount: undefined,
          languageMessages: {
            vi: { ipLimitMessage: '', deviceLimitMessage: '' },
            en: { ipLimitMessage: '', deviceLimitMessage: '' },
            zh: { ipLimitMessage: '', deviceLimitMessage: '' },
          },
        },
      });
    }
    setDrawerVisible(true);
  };

  const handleDelete = (record: ActivityRecord) => {
    setRecords(prev => prev.filter(item => item.id !== record.id));
    message.success(`已删除活动「${record.title.zh || record.type}」`);
  };

  const handleDrawerSubmit = async () => {
    const values = await editorForm.validateFields();
    values.guide = {
      ...(values.guide || {}),
      payoutMethod: values.guide?.payoutMethod || 'auto',
    };
    values.wheel = { ...(values.wheel || {}), rewardType: values.wheelRewardType };
    values.redRain = { ...(values.redRain || {}), rewardType: values.redRainRewardType };
    values.signIn = {
      ...(values.signIn || {}),
      rewardType: values.signInRewardType,
      payoutMethod: values.signIn?.payoutMethod || 'auto',
    };

    const payload: ActivityRecord = {
      id: editingRecord ? editingRecord.id : Date.now(),
      category: values.category || '全部',
      module: values.module || '代理',
      type: values.type || '直播活动',
      description: values.description || '',
      sortOrder: values.sortOrder || 1,
      allowRepeat: values.allowRepeat ?? false,
      title: {
        local: values.langs?.vi?.title || values.langs?.en?.title || values.langs?.zh?.title || '',
        en: values.langs?.en?.title || '',
        zh: values.langs?.zh?.title || '',
      },
      jumpType: values.jumpType || '-',
      status: values.status,
      validity: values.validity === 'permanent'
        ? '永久有效'
        : dayjs(values.effectiveDate).format('YYYY-MM-DD HH:mm:ss'),
      creator: editingRecord ? editingRecord.creator : 'admin',
      createTime: editingRecord ? editingRecord.createTime : dayjs().format('YYYY-MM-DD HH:mm:ss'),
      updater: 'admin',
      updateTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      userScope: values.userScope || '全部',
      visibleTags: values.visibleTags || undefined,
      rewardTags: values.rewardTags || undefined,
      claimConditions: values.claimConditions ? {
        sameIpRewardCount: values.claimConditions.sameIpRewardCount || undefined,
        sameDeviceRewardCount: values.claimConditions.sameDeviceRewardCount || undefined,
        languageMessages: {
          vi: {
            ipLimitMessage: values.claimConditions.languageMessages?.vi?.ipLimitMessage || '',
            deviceLimitMessage: values.claimConditions.languageMessages?.vi?.deviceLimitMessage || '',
          },
          en: {
            ipLimitMessage: values.claimConditions.languageMessages?.en?.ipLimitMessage || '',
            deviceLimitMessage: values.claimConditions.languageMessages?.en?.deviceLimitMessage || '',
          },
          zh: {
            ipLimitMessage: values.claimConditions.languageMessages?.zh?.ipLimitMessage || '',
            deviceLimitMessage: values.claimConditions.languageMessages?.zh?.deviceLimitMessage || '',
          },
        },
      } : undefined,
      config: {
        template: values.template,
        guide: values.guide,
        guideTasks: values.guideTasks,
        guideRewardType: values.guideRewardType,
        guideCoupon: values.guideCoupon,
        wheel: values.wheel,
        wheelRewards: values.wheelRewards,
        wheelRewardType: values.wheelRewardType,
        wheelCoupon: values.wheelCoupon,
        unregisteredRewardType: values.unregisteredRewardType,
        redRain: values.redRain,
        redRainRewards: values.redRainRewards,
        redRainRewardType: values.redRainRewardType,
        redRainCoupon: values.redRainCoupon,
        signInRewards: values.signInRewards,
        signInRewardType: values.signInRewardType,
        signInCoupon: values.signInCoupon,
        onlineDuration: values.onlineDuration,
        onlineDurationRewardType: values.onlineDurationRewardType,
        downloadApp: values.downloadApp,
        guideEgameCoupon: values.guideEgameCoupon,
        wheelEgameCoupon: values.wheelEgameCoupon,
        redRainEgameCoupon: values.redRainEgameCoupon,
        signInEgameCoupon: values.signInEgameCoupon,
        egameReward: values.egameReward,
        langs: values.langs,
      },
    };

    setRecords(prev => {
      if (editingRecord) {
        return prev.map(item => (item.id === editingRecord.id ? payload : item));
      }
      return [payload, ...prev];
    });

    message.success(editingRecord ? '活动已更新' : '活动已创建');
    setDrawerVisible(false);
  };

  const toolbarButtons = (
    <Space>
      <Button icon={<ReloadOutlined />} onClick={() => message.info('已重置筛选条件')} />
      <Button icon={<SaveOutlined />} onClick={() => message.success('缓存查询成功')}>
        缓存查询
      </Button>
      <Button onClick={() => message.success('缓存已重置')}>重置缓存</Button>
      <Button danger onClick={() => message.success('缓存已清除')}>
        清除缓存
      </Button>
    </Space>
  );

  const renderRewardLadder = (fieldName: string, labels?: { min?: string; max?: string; multiplier?: string }) => (
    <Form.List name={fieldName}>
      {(fields, { add, remove }) => (
        <>
          <Row gutter={16} style={{ fontWeight: 500 }}>
            <Col span={6}>{labels?.min || '邀请人数范围(小)'}</Col>
            <Col span={6}>{labels?.max || '邀请人数范围(大)'}</Col>
            <Col span={6}>{labels?.multiplier || '奖励倍数'}</Col>
            <Col span={6}>操作</Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="活动描述" name="description" rules={[{ required: true }]}>
                <Input placeholder="请输入活动描述" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="活动排序" name="sortOrder" rules={[{ required: true }]}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="是否复投" name="allowRepeat" rules={[{ required: true }]}>
                <Radio.Group>
                  <Radio value={true}>是</Radio>
                  <Radio value={false}>否</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
          {fields.map(field => (
            <Row gutter={16} key={field.key} style={{ marginBottom: 12 }}>
              <Col span={6}>
                <Form.Item
                  name={[field.name, 'min']}
                  rules={[{ required: true, message: '请输入最小范围' }]}
                >
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name={[field.name, 'max']}
                  rules={[{ required: true, message: '请输入最大范围' }]}
                >
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name={[field.name, 'multiplier']}
                  rules={[{ required: true, message: '请输入奖励倍数' }]}
                >
                  <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Button danger type="link" onClick={() => remove(field.name)}>
                  删除
                </Button>
              </Col>
            </Row>
          ))}
          <Button type="dashed" onClick={() => add({ min: 0, max: 0, multiplier: 1 })} block icon={<PlusOutlined />}>
            新增阶梯
          </Button>
        </>
      )}
    </Form.List>
  );

  const renderWheelTemplate = () => {
    const rewardType = wheelRewardType || 'cash';
    return (
      <>
      <Divider orientation="left">已注册用户奖励设置</Divider>
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label="奖励类型" name="wheelRewardType" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'cash', label: '真金' },
                { value: 'percentage', label: '百分比' },
                { value: 'coupon', label: '直播游戏券' },
                { value: 'egameCoupon', label: '电子游戏券' },
              ]}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="派奖方式" name={['wheel', 'payoutMethod']} rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'auto', label: '自动派发' },
                { value: 'manual', label: '手动领取' },
              ]}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="领取金币是否需要充值" name={['wheel', 'requireDeposit']} valuePropName="checked">
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>
        </Col>
      </Row>
      {rewardType === 'cash' || rewardType === 'percentage' ? (
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label={rewardType === 'percentage' ? '最低充值金额(VND)' : '最低充值金额(VND)'} name={['wheel', 'minRecharge']} rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label={rewardType === 'percentage' ? '奖励百分比最小值(%)' : '抽奖奖励最小值(VND)'} name={['wheel', rewardType === 'percentage' ? 'drawMinPercent' : 'drawMin']} rules={[{ required: true }]}>
              <InputNumber min={0} max={rewardType === 'percentage' ? 100 : undefined} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label={rewardType === 'percentage' ? '奖励百分比最大值(%)' : '抽奖奖励最大值(VND)'} name={['wheel', rewardType === 'percentage' ? 'drawMaxPercent' : 'drawMax']} rules={[{ required: true }]}>
              <InputNumber min={0} max={rewardType === 'percentage' ? 100 : undefined} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
      ) : rewardType === 'coupon' ? (
        renderCouponConfig('wheelCoupon', wheelPayoutMethod !== 'auto', '直播游戏券')
      ) : (
        renderCouponConfig('wheelEgameCoupon', wheelPayoutMethod !== 'auto', '电子游戏券')
      )}

      <Divider orientation="left">未注册用户奖励设置</Divider>
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label="奖励类型" name="unregisteredRewardType" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'cash', label: '真金' },
                { value: 'coupon', label: '直播游戏券' },
                { value: 'egameCoupon', label: '电子游戏券' },
              ]}
            />
          </Form.Item>
        </Col>
      </Row>
      {unregisteredRewardType === 'cash' ? (
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="未注册用户最低奖励(VND)" name={['wheel', 'unregisteredMinReward']} rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="未注册用户最高奖励(VND)" name={['wheel', 'unregisteredMaxReward']} rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
      ) : unregisteredRewardType === 'coupon' ? (
        <>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="直播游戏券面值(VND)"
                name={['wheel', 'unregisteredCouponValue']}
                rules={[{ required: true }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="未注册用户直播游戏券张数下限" name={['wheel', 'unregisteredCouponMinCount']} rules={[{ required: true }]}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="未注册用户直播游戏券张数上限" name={['wheel', 'unregisteredCouponMaxCount']} rules={[{ required: true }]}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="使用有效期(分钟)" name={['wheel', 'unregisteredCouponUsageExpireMinutes']} rules={[{ required: true }]}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Divider orientation="left">提现限制配置</Divider>
          <Form.List name={['wheel', 'unregisteredCouponWithdrawLimits']}>
            {(fields, { add, remove }) => (
              <>
                <Row gutter={16} style={{ fontWeight: 500 }}>
                  <Col span={10}>充值(VND)</Col>
                  <Col span={10}>可提(VND)</Col>
                  <Col span={4}>操作</Col>
                </Row>
                {fields.map(field => (
                  <Row gutter={16} key={field.key} style={{ marginBottom: 12 }}>
                    <Col span={10}>
                      <Form.Item name={[field.name, 'recharge']} rules={[{ required: true }]}>
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={10}>
                      <Form.Item name={[field.name, 'withdrawable']} rules={[{ required: true }]}>
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Button type="link" danger onClick={() => remove(field.name)}>
                        删除
                      </Button>
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add({ recharge: 0, withdrawable: 0 })} block icon={<PlusOutlined />}>
                  新增限制
                </Button>
              </>
            )}
          </Form.List>
        </>
      ) : (
        <>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="电子游戏券面值(VND)"
                name={['wheel', 'unregisteredEgameCouponValue']}
                rules={[{ required: true }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="未注册用户电子游戏券张数下限" name={['wheel', 'unregisteredEgameCouponMinCount']} rules={[{ required: true }]}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="未注册用户电子游戏券张数上限" name={['wheel', 'unregisteredEgameCouponMaxCount']} rules={[{ required: true }]}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="使用有效期(分钟)" name={['wheel', 'unregisteredEgameCouponUsageExpireMinutes']} rules={[{ required: true }]}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Divider orientation="left">提现限制配置</Divider>
          <Form.List name={['wheel', 'unregisteredEgameCouponWithdrawLimits']}>
            {(fields, { add, remove }) => (
              <>
                <Row gutter={16} style={{ fontWeight: 500 }}>
                  <Col span={10}>充值(VND)</Col>
                  <Col span={10}>可提(VND)</Col>
                  <Col span={4}>操作</Col>
                </Row>
                {fields.map(field => (
                  <Row gutter={16} key={field.key} style={{ marginBottom: 12 }}>
                    <Col span={10}>
                      <Form.Item name={[field.name, 'recharge']} rules={[{ required: true }]}>
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={10}>
                      <Form.Item name={[field.name, 'withdrawable']} rules={[{ required: true }]}>
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Button type="link" danger onClick={() => remove(field.name)}>
                        删除
                      </Button>
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add({ recharge: 0, withdrawable: 0 })} block icon={<PlusOutlined />}>
                  新增限制
                </Button>
              </>
            )}
          </Form.List>
        </>
      )}
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label="积分倍率" name={['wheel', 'multiplier']} rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={16}>
          <Form.Item label="跳转参与用户" name={['wheel', 'joinUserType']} rules={[{ required: true }]}>
            <Radio.Group>
              <Radio value="unregistered">未注册</Radio>
              <Radio value="registered">已注册</Radio>
            </Radio.Group>
          </Form.Item>
          <Space>
            每
            <Form.Item noStyle name={['wheel', 'joinCycleDays']} rules={[{ required: true }]}>
              <InputNumber min={1} style={{ width: 80 }} />
            </Form.Item>
            天参与
            <Form.Item noStyle name={['wheel', 'joinTimesPerCycle']} rules={[{ required: true }]}>
              <InputNumber min={1} style={{ width: 80 }} />
            </Form.Item>
            次
          </Space>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={6}>
          <Form.Item label="是否展示在活动列表" name={['wheel', 'showInList']} valuePropName="checked">
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="web首页固定显示" name={['wheel', 'showOnHome']} valuePropName="checked">
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="邀请注册奖金膨胀" name={['wheel', 'enableInflation']} valuePropName="checked">
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="奖金领取限时(分钟)" name={['wheel', 'claimLimitMinutes']} rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="膨胀限时(分钟)" name={['wheel', 'inflationMinutes']} rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="最大膨胀奖金领取限制(VND)" name={['wheel', 'inflationMax']} rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left">膨胀奖励阶梯设置</Divider>
      {renderRewardLadder('wheelRewards')}

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={12}>
          <Form.Item label="同设备限制账号个数" name={['wheel', 'sameDeviceLimit']} rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="同IP限制账号个数" name={['wheel', 'sameIpLimit']} rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
  };

  const renderRedRainTemplate = () => {
    const rewardType = redRainRewardType || 'cash';
    return (
      <>
      <Divider orientation="left">红包雨设置</Divider>
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label="奖励类型" name="redRainRewardType" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'cash', label: '真金' },
                { value: 'percentage', label: '百分比' },
                { value: 'coupon', label: '直播游戏券' },
                { value: 'egameCoupon', label: '电子游戏券' },
              ]}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="派奖方式" name={['redRain', 'payoutMethod']} rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'auto', label: '自动派发' },
                { value: 'manual', label: '手动领取' },
              ]}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="周期内爆量红包个数" name={['redRain', 'burstCount']} rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
      {rewardType === 'cash' || rewardType === 'percentage' ? (
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item label={rewardType === 'percentage' ? '奖励百分比最小值(%)' : '每个红包最小值(VND)'} name={['redRain', rewardType === 'percentage' ? 'minRewardPercent' : 'minReward']} rules={[{ required: true }]}>
              <InputNumber min={0} max={rewardType === 'percentage' ? 100 : undefined} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label={rewardType === 'percentage' ? '奖励百分比最大值(%)' : '每个红包最大值(VND)'} name={['redRain', rewardType === 'percentage' ? 'maxRewardPercent' : 'maxReward']} rules={[{ required: true }]}>
              <InputNumber min={0} max={rewardType === 'percentage' ? 100 : undefined} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="积分倍率" name={['redRain', 'multiplier']} rules={[{ required: true }]}>
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="红包雨时长(秒)" name={['redRain', 'rainDuration']} rules={[{ required: true }]}>
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
      ) : rewardType === 'coupon' ? (
        renderCouponConfig('redRainCoupon', redRainPayoutMethod !== 'auto', '直播游戏券')
      ) : (
        renderCouponConfig('redRainEgameCoupon', redRainPayoutMethod !== 'auto', '电子游戏券')
      )}

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label="注册大于(天)" name={['redRain', 'registerDays']} rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="未登录大于(天)" name={['redRain', 'inactiveDays']} rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="每多少天参与1次" name={['redRain', 'participationIntervalDays']} rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={6}>
          <Form.Item label="是否展示在活动列表" name={['redRain', 'showInList']} valuePropName="checked">
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="web首页固定显示" name={['redRain', 'showOnHome']} valuePropName="checked">
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="邀请注册奖金膨胀" name={['redRain', 'enableInflation']} valuePropName="checked">
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="奖金领取限时(分钟)" name={['redRain', 'claimLimitMinutes']} rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label="膨胀限时(分钟)" name={['redRain', 'inflationMinutes']} rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="最大膨胀奖金(VND)" name={['redRain', 'inflationMax']} rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left">膨胀奖励阶梯设置</Divider>
      {renderRewardLadder('redRainRewards', { min: '邀请人数范围(小)', max: '邀请人数范围(大)', multiplier: '奖励倍数(VND)' })}

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={12}>
          <Form.Item label="同设备限制账号个数" name={['redRain', 'sameDeviceLimit']} rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="同IP限制账号个数" name={['redRain', 'sameIpLimit']} rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>

    </>
  );
  };

  const renderSignInTemplate = () => {
    const rewardType = signInRewardType || 'cash';
    return (
      <>
      <Divider orientation="left">签到奖励配置</Divider>
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label="奖励类型" name="signInRewardType" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'cash', label: '真金' },
                { value: 'percentage', label: '百分比' },
                { value: 'coupon', label: '直播游戏券' },
                { value: 'egameCoupon', label: '电子游戏券' },
              ]}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="派奖方式" name={['signIn', 'payoutMethod']} rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'auto', label: '自动派发' },
                { value: 'manual', label: '手动领取' },
              ]}
            />
          </Form.Item>
        </Col>
      </Row>
      {rewardType === 'coupon' && renderCouponConfig('signInCoupon', signInPayoutMethod !== 'auto', '直播游戏券')}
      {rewardType === 'egameCoupon' && renderCouponConfig('signInEgameCoupon', signInPayoutMethod !== 'auto', '电子游戏券')}
      <Form.List name="signInRewards">
        {(fields, { add, remove }) => (
          <>
            <Row gutter={16} style={{ fontWeight: 500 }}>
              <Col span={8}>天数</Col>
              <Col span={12}>
                {rewardType === 'cash'
                  ? '奖励(VND)'
                  : rewardType === 'percentage'
                    ? '奖励百分比(%)'
                    : rewardType === 'coupon'
                      ? '奖励直播游戏券(张)'
                      : '奖励电子游戏券(张)'}
              </Col>
              <Col span={4}>操作</Col>
            </Row>
            {fields.map(field => (
              <Row gutter={16} key={field.key} style={{ marginBottom: 12 }}>
                <Col span={8}>
                  <Form.Item name={[field.name, 'day']} rules={[{ required: true }]}>
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  {rewardType === 'cash' ? (
                    <Form.Item name={[field.name, 'reward']} rules={[{ required: true }]}>
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                  ) : rewardType === 'percentage' ? (
                    <Form.Item name={[field.name, 'rewardPercentage']} rules={[{ required: true }]}>
                      <InputNumber min={0} max={100} style={{ width: '100%' }} />
                    </Form.Item>
                  ) : rewardType === 'coupon' ? (
                    <Form.Item name={[field.name, 'rewardCouponCount']} rules={[{ required: true }]}>
                      <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                  ) : (
                    <Form.Item name={[field.name, 'rewardEgameCouponCount']} rules={[{ required: true }]}>
                      <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                  )}
                </Col>
                <Col span={4}>
                  <Button type="link" danger onClick={() => remove(field.name)}>
                    删除
                  </Button>
                </Col>
              </Row>
            ))}
            <Button type="dashed" onClick={() => add({ day: (fields.length || 0) + 1, reward: 0 })} block icon={<PlusOutlined />}>
              新增签到天数
            </Button>
          </>
        )}
      </Form.List>

    </>
  );
  };

  const renderOnlineDurationTemplate = () => {
    const rewardType = onlineDurationRewardType || 'cash';
    return (
      <>
        <Divider orientation="left">奖励配置</Divider>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="奖励类型" name="onlineDurationRewardType" rules={[{ required: true }]}>
              <Select
                options={[
                  { value: 'cash', label: '真金' },
                  { value: 'coupon', label: '直播游戏券' },
                  { value: 'egameCoupon', label: '电子游戏券' },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="派奖方式" name={['onlineDuration', 'payoutMethod']} rules={[{ required: true }]}>
              <Select
                options={[
                  { value: 'auto', label: '自动派发' },
                  { value: 'manual', label: '手动领取' },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="是否展示在活动列表" name={['onlineDuration', 'showInList']} valuePropName="checked">
              <Radio.Group>
                <Radio value={true}>是</Radio>
                <Radio value={false}>否</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="在线时长(分钟)" name={['onlineDuration', 'durationMinutes']} rules={[{ required: true }]}>
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="转盘次数" name={['onlineDuration', 'turntableSpins']} rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="参与比例" name={['onlineDuration', 'participationRatio']} rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="彩金提现稽核倍数" name={['onlineDuration', 'auditMultiplier']} rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        {rewardType === 'cash' ? (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="奖励金额(VND)" name={['onlineDuration', 'rewardAmount']} rules={[{ required: true }]}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        ) : rewardType === 'coupon' ? (
          <>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="直播游戏券金额(VND)"
                  name={['onlineDuration', 'couponValue']}
                  rules={[{ required: true }]}
                >
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="奖励多少直播游戏券"
                  name={['onlineDuration', 'couponCount']}
                  rules={[{ required: true }]}
                >
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              {onlineDurationPayoutMethod === 'manual' && (
                <Col span={12}>
                  <Form.Item
                    label="手动领取时间(分钟)"
                    name={['onlineDuration', 'claimExpireMinutes']}
                    rules={[{ required: true }]}
                  >
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              )}
              <Col span={onlineDurationPayoutMethod === 'manual' ? 12 : 24}>
                <Form.Item
                  label="使用有效期(分钟)"
                  name={['onlineDuration', 'usageExpireMinutes']}
                  rules={[{ required: true }]}
                >
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Divider orientation="left">提现限制配置</Divider>
            <Form.List name={['onlineDuration', 'withdrawLimits']}>
              {(fields, { add, remove }) => (
                <>
                  <Row gutter={16} style={{ fontWeight: 500 }}>
                    <Col span={10}>充值(VND)</Col>
                    <Col span={10}>可提(VND)</Col>
                    <Col span={4}>操作</Col>
                  </Row>
                  {fields.map(field => (
                    <Row gutter={16} key={field.key} style={{ marginBottom: 12 }}>
                      <Col span={10}>
                        <Form.Item name={[field.name, 'recharge']} rules={[{ required: true }]}>
                          <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={10}>
                        <Form.Item name={[field.name, 'withdrawable']} rules={[{ required: true }]}>
                          <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Button type="link" danger onClick={() => remove(field.name)}>
                          删除
                        </Button>
                      </Col>
                    </Row>
                  ))}
                  <Button type="dashed" onClick={() => add({ recharge: 0, withdrawable: 0 })} block icon={<PlusOutlined />}>
                    新增限制
                  </Button>
                </>
              )}
            </Form.List>
          </>
        ) : (
          <>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="电子游戏券金额(VND)"
                  name={['onlineDuration', 'egameCouponValue']}
                  rules={[{ required: true }]}
                >
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="奖励多少电子游戏券"
                  name={['onlineDuration', 'egameCouponCount']}
                  rules={[{ required: true }]}
                >
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              {onlineDurationPayoutMethod === 'manual' && (
                <Col span={12}>
                  <Form.Item
                    label="手动领取时间(分钟)"
                    name={['onlineDuration', 'egameClaimExpireMinutes']}
                    rules={[{ required: true }]}
                  >
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              )}
              <Col span={onlineDurationPayoutMethod === 'manual' ? 12 : 24}>
                <Form.Item
                  label="使用有效期(分钟)"
                  name={['onlineDuration', 'egameUsageExpireMinutes']}
                  rules={[{ required: true }]}
                >
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Divider orientation="left">提现限制配置</Divider>
            <Form.List name={['onlineDuration', 'egameWithdrawLimits']}>
              {(fields, { add, remove }) => (
                <>
                  <Row gutter={16} style={{ fontWeight: 500 }}>
                    <Col span={10}>充值(VND)</Col>
                    <Col span={10}>可提(VND)</Col>
                    <Col span={4}>操作</Col>
                  </Row>
                  {fields.map(field => (
                    <Row gutter={16} key={field.key} style={{ marginBottom: 12 }}>
                      <Col span={10}>
                        <Form.Item name={[field.name, 'recharge']} rules={[{ required: true }]}>
                          <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={10}>
                        <Form.Item name={[field.name, 'withdrawable']} rules={[{ required: true }]}>
                          <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Button type="link" danger onClick={() => remove(field.name)}>
                          删除
                        </Button>
                      </Col>
                    </Row>
                  ))}
                  <Button type="dashed" onClick={() => add({ recharge: 0, withdrawable: 0 })} block icon={<PlusOutlined />}>
                    新增限制
                  </Button>
                </>
              )}
            </Form.List>
          </>
        )}
      </>
    );
  };

  const renderDownloadAppTemplate = () => {
    return (
      <>
        <Divider orientation="left">下载选项配置</Divider>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="下载选项" name={['downloadApp', 'enableApp']} valuePropName="checked">
              <Switch checkedChildren="下载APP" unCheckedChildren="不启用" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="下载选项" name={['downloadApp', 'enablePwa']} valuePropName="checked">
              <Switch checkedChildren="下载PWA" unCheckedChildren="不启用" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="派发方式" name={['downloadApp', 'payoutMethod']} rules={[{ required: true }]}>
              <Select
                options={[
                  { value: 'auto', label: '自动派发' },
                  { value: 'manual', label: '手动领取' },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="有效时效内领取(小时)" name={['downloadApp', 'claimExpireHours']} rules={[{ required: true }]}>
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="稽核倍数" name={['downloadApp', 'auditMultiplier']} rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="是否展示在活动列表" name={['downloadApp', 'showInList']} valuePropName="checked">
              <Radio.Group>
                <Radio value={true}>是</Radio>
                <Radio value={false}>否</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="web首页固定展示" name={['downloadApp', 'showOnHome']} valuePropName="checked">
              <Radio.Group>
                <Radio value={true}>是</Radio>
                <Radio value={false}>否</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        {downloadAppEnabled && !downloadPwaEnabled && (
          <>
            <Divider orientation="left">下载APP奖励配置</Divider>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="奖励金额(金币)" name={['downloadApp', 'appRewardAmount']} rules={[{ required: true }]}>
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="最高提现金额" name={['downloadApp', 'appMaxWithdraw']} rules={[{ required: true }]}>
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {!downloadAppEnabled && downloadPwaEnabled && (
          <>
            <Divider orientation="left">下载PWA奖励配置</Divider>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="奖励金额(金币)" name={['downloadApp', 'pwaRewardAmount']} rules={[{ required: true }]}>
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="最高提现金额" name={['downloadApp', 'pwaMaxWithdraw']} rules={[{ required: true }]}>
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {downloadAppEnabled && downloadPwaEnabled && (
          <>
            <Divider orientation="left">下载APP奖励配置</Divider>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="奖励金额(金币)" name={['downloadApp', 'appRewardAmount']} rules={[{ required: true }]}>
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="最高提现金额" name={['downloadApp', 'appMaxWithdraw']} rules={[{ required: true }]}>
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Divider orientation="left">下载PWA奖励配置</Divider>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="奖励金额(金币)" name={['downloadApp', 'pwaRewardAmount']} rules={[{ required: true }]}>
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="最高提现金额" name={['downloadApp', 'pwaMaxWithdraw']} rules={[{ required: true }]}>
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
      </>
    );
  };

  const renderEgameRewardTemplate = () => {
    return (
      <>
        <Divider orientation="left">电子活动奖励配置</Divider>
        <Alert
          type="info"
          showIcon
          message="结算规则"
          description="每天 00:00 按前一日有效投注结算，达到档位发放对应奖励。"
          style={{ marginBottom: 16 }}
        />
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="参与厂商" name={['egameReward', 'venues']} rules={[{ required: true }]}>
              <Select mode="multiple" placeholder="选择参与活动的厂商">
                {VENUE_OPTIONS.map(venue => (
                  <Option key={venue.value} value={venue.value}>
                    {venue.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="每日结算时间" name={['egameReward', 'settleTime']} rules={[{ required: true }]}>
              <Input placeholder="00:00" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="打码倍数" name={['egameReward', 'auditMultiplier']} rules={[{ required: true }]}>
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="最高奖励金额" name={['egameReward', 'maxReward']} rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="规则说明" name={['egameReward', 'ruleNote']} rules={[{ required: true }]}>
          <Input.TextArea rows={3} placeholder="描述活动规则与限制条件" />
        </Form.Item>

        <Divider orientation="left">投注返还梯度</Divider>
        <Form.List name={['egameReward', 'rewardLadders']}>
          {(fields, { add, remove }) => (
            <>
              <Row gutter={16} style={{ fontWeight: 500 }}>
                <Col span={10}>有效投注金额</Col>
                <Col span={10}>返还金额</Col>
                <Col span={4}>操作</Col>
              </Row>
              {fields.map(field => (
                <Row gutter={16} key={field.key} style={{ marginBottom: 12 }}>
                  <Col span={10}>
                    <Form.Item name={[field.name, 'minBet']} rules={[{ required: true }]}>
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={10}>
                    <Form.Item name={[field.name, 'reward']} rules={[{ required: true }]}>
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Button type="link" danger onClick={() => remove(field.name)}>
                      删除
                    </Button>
                  </Col>
                </Row>
              ))}
              <Button type="dashed" onClick={() => add({ minBet: 0, reward: 0 })} block icon={<PlusOutlined />}>
                新增梯度
              </Button>
            </>
          )}
        </Form.List>
      </>
    );
  };

  const columns = [
    {
      title: '序号',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      align: 'center' as const,
      render: (_: any, __: ActivityRecord, index: number) => index + 1,
    },
    {
      title: '活动大类',
      dataIndex: 'category',
      key: 'category',
      width: 120,
    },
    {
      title: '活动模块',
      dataIndex: 'module',
      key: 'module',
      width: 120,
    },
    {
      title: '活动类型',
      dataIndex: 'type',
      key: 'type',
      width: 180,
    },
    {
      title: '活动标题',
      dataIndex: 'title',
      key: 'title',
      width: 220,
      render: (title: ActivityRecord['title']) => (
        <div>
          <Text type="secondary" style={{ display: 'block' }}>{title.local}</Text>
          <Text type="secondary" style={{ display: 'block' }}>{title.en}</Text>
          <Text type="secondary" style={{ display: 'block' }}>{title.zh}</Text>
        </div>
      ),
    },
    {
      title: '跳转类型',
      dataIndex: 'jumpType',
      key: 'jumpType',
      width: 140,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center' as const,
      render: (status: ActivityStatus) => (
        <Tag color={STATUS_MAP[status].color}>{STATUS_MAP[status].text}</Tag>
      ),
    },
    {
      title: '活动有效期',
      dataIndex: 'validity',
      key: 'validity',
      width: 140,
    },
    {
      title: '可见标签',
      dataIndex: 'visibleTags',
      key: 'visibleTags',
      width: 200,
      render: (tags: number[] | undefined) => {
        if (!tags || tags.length === 0) {
          return <span style={{ color: '#999' }}>全部可见</span>;
        }
        return (
          <Space size={[0, 8]} wrap>
            {tags.map(tagId => {
              const tag = MOCK_TAGS.find(t => t.id === tagId);
              return tag ? (
                <Tag key={tagId} color={tag.color}>{tag.name}</Tag>
              ) : null;
            })}
          </Space>
        );
      },
    },
    {
      title: '可领奖标签',
      dataIndex: 'rewardTags',
      key: 'rewardTags',
      width: 200,
      render: (tags: number[] | undefined) => {
        if (!tags || tags.length === 0) {
          return <span style={{ color: '#999' }}>无限制</span>;
        }
        return (
          <Space size={[0, 8]} wrap>
            {tags.map(tagId => {
              const tag = MOCK_TAGS.find(t => t.id === tagId);
              return tag ? (
                <Tag key={tagId} color={tag.color}>{tag.name}</Tag>
              ) : null;
            })}
          </Space>
        );
      },
    },
    {
      title: '创建人',
      dataIndex: 'creator',
      key: 'creator',
      width: 120,
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 170,
    },
    {
      title: '修改人',
      dataIndex: 'updater',
      key: 'updater',
      width: 120,
    },
    {
      title: '更新时间',
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: 170,
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right' as const,
      width: 140,
      render: (_: any, record: ActivityRecord) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openDrawer(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确认删除该活动？"
            onConfirm={() => handleDelete(record)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

const renderLanguageSection = () => (
  <>
    <Divider orientation="left">语言配置</Divider>
    <Tabs defaultActiveKey="vi">
      {LANGUAGE_TABS.map(tab => (
        <TabPane tab={tab.label} key={tab.key}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="活动标题" name={['langs', tab.key, 'title']} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="跳转链接" name={['langs', tab.key, 'jumpLink']}>
            <Input placeholder="请输入跳转链接" />
          </Form.Item>
          <Form.Item label="富文本活动详情" name={['langs', tab.key, 'richText']}>
            <Input.TextArea rows={5} placeholder="支持粘贴富文本内容" />
          </Form.Item>
          <Divider orientation="left" style={{ marginTop: 24, marginBottom: 16 }}>领取限制提示信息</Divider>
          <Form.Item 
            label="IP奖励限制提示" 
            name={['claimConditions', 'languageMessages', tab.key, 'ipLimitMessage']}
            extra="当用户因同IP奖励数量限制无法领取时显示的提示信息"
          >
            <Input.TextArea 
              rows={2} 
              placeholder={`例如：该IP地址下的奖励领取数量已达上限，无法继续领取`} 
            />
          </Form.Item>
          <Form.Item 
            label="设备奖励限制提示" 
            name={['claimConditions', 'languageMessages', tab.key, 'deviceLimitMessage']}
            extra="当用户因同设备奖励数量限制无法领取时显示的提示信息"
          >
            <Input.TextArea 
              rows={2} 
              placeholder={`例如：该设备下的奖励领取数量已达上限，无法继续领取`} 
            />
          </Form.Item>
        </TabPane>
      ))}
    </Tabs>
  </>
);

  const renderCouponConfig = (fieldPath: string, showClaimTime = true, couponTypeName = '直播游戏券') => (
    <>
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            label={`${couponTypeName}金额(VND)`}
            name={[fieldPath, 'couponValue']}
            rules={[{ required: true }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        {showClaimTime && (
          <Col span={12}>
            <Form.Item
              label="手动领取时间(分钟)"
              name={[fieldPath, 'claimExpireMinutes']}
              rules={[{ required: true }]}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        )}
        <Col span={showClaimTime ? 12 : 24}>
          <Form.Item
            label="使用有效期(分钟)"
            name={[fieldPath, 'usageExpireMinutes']}
            rules={[{ required: true }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
      <Divider orientation="left">提现限制配置</Divider>
      <Form.List name={[fieldPath, 'withdrawLimits']}>
        {(fields, { add, remove }) => (
          <>
            <Row gutter={16} style={{ fontWeight: 500 }}>
              <Col span={10}>充值(VND)</Col>
              <Col span={10}>可提(VND)</Col>
              <Col span={4}>操作</Col>
            </Row>
            {fields.map(field => (
              <Row gutter={16} key={field.key} style={{ marginBottom: 12 }}>
                <Col span={10}>
                  <Form.Item name={[field.name, 'recharge']} rules={[{ required: true }]}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item name={[field.name, 'withdrawable']} rules={[{ required: true }]}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Button type="link" danger onClick={() => remove(field.name)}>
                    删除
                  </Button>
                </Col>
              </Row>
            ))}
            <Button type="dashed" onClick={() => add({ recharge: 0, withdrawable: 0 })} block icon={<PlusOutlined />}>
              新增限制
            </Button>
          </>
        )}
      </Form.List>

      <Divider orientation="left">跳转游戏配置</Divider>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="跳转类型"
            name={[fieldPath, 'jumpGameType']}
            rules={[{ required: true, message: '请选择跳转类型' }]}
            extra="选择跳转到厂商还是子游戏"
          >
            <Select placeholder="请选择跳转类型">
              <Option value="venue">只配置厂商</Option>
              <Option value="game">配置子游戏</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Form.Item noStyle shouldUpdate={(prev, curr) => {
        const prevType = prev?.[fieldPath]?.jumpGameType;
        const currType = curr?.[fieldPath]?.jumpGameType;
        return prevType !== currType;
      }}>
        {({ getFieldValue }) => {
          const jumpGameType = getFieldValue([fieldPath, 'jumpGameType']);
          
          if (jumpGameType === 'venue') {
            // 只配置厂商
            return (
              <Form.Item
                label="选择厂商"
                name={[fieldPath, 'jumpVenueCodes']}
                rules={[{ required: true, message: '请至少选择一个厂商' }]}
                extra="可选择多个厂商，随机跳转"
              >
                <Select mode="multiple" placeholder="请选择厂商">
                  {VENUE_OPTIONS.map(venue => (
                    <Option key={venue.value} value={venue.value}>{venue.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            );
          } else if (jumpGameType === 'game') {
            // 配置子游戏
            return (
              <>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="选择厂商"
                      name={[fieldPath, 'jumpGameVenueCode']}
                      rules={[{ required: true, message: '请选择厂商' }]}
                    >
                      <Select 
                        placeholder="请选择厂商"
                        onChange={() => {
                          // 清空子游戏选择
                          editorForm.setFieldsValue({
                            [fieldPath]: {
                              ...getFieldValue(fieldPath),
                              jumpGameCode: undefined,
                              jumpGameCodes: undefined,
                              jumpGameIsLobby: undefined,
                            },
                          });
                        }}
                      >
                        {VENUE_OPTIONS.map(venue => (
                          <Option key={venue.value} value={venue.value}>{venue.label}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="子游戏类型"
                      name={[fieldPath, 'jumpGameIsLobby']}
                      rules={[{ required: true, message: '请选择子游戏类型' }]}
                    >
                      <Radio.Group>
                        <Radio value={true}>大厅</Radio>
                        <Radio value={false}>子游戏</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item noStyle shouldUpdate={(prev, curr) => {
                  const prevVenue = prev?.[fieldPath]?.jumpGameVenueCode;
                  const currVenue = curr?.[fieldPath]?.jumpGameVenueCode;
                  const prevIsLobby = prev?.[fieldPath]?.jumpGameIsLobby;
                  const currIsLobby = curr?.[fieldPath]?.jumpGameIsLobby;
                  return prevVenue !== currVenue || prevIsLobby !== currIsLobby;
                }}>
                  {({ getFieldValue }) => {
                    const selectedVenue = getFieldValue([fieldPath, 'jumpGameVenueCode']);
                    const isLobby = getFieldValue([fieldPath, 'jumpGameIsLobby']);
                    const games = selectedVenue ? (MOCK_GAMES[selectedVenue] || []) : [];

                    if (isLobby === true) {
                      // 大厅，不需要选择子游戏
                      return null;
                    } else if (isLobby === false) {
                      // 子游戏，需要选择单个或多个
                      return (
                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item
                              label="子游戏数量"
                              name={[fieldPath, 'jumpGameCount']}
                              rules={[{ required: true, message: '请选择子游戏数量' }]}
                            >
                              <Radio.Group>
                                <Radio value={1}>单个子游戏</Radio>
                                <Radio value={2}>多个子游戏（随机跳转）</Radio>
                              </Radio.Group>
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item noStyle shouldUpdate={(prev, curr) => {
                              const prevCount = prev?.[fieldPath]?.jumpGameCount;
                              const currCount = curr?.[fieldPath]?.jumpGameCount;
                              return prevCount !== currCount;
                            }}>
                              {({ getFieldValue }) => {
                                const gameCount = getFieldValue([fieldPath, 'jumpGameCount']);
                                if (gameCount === 1) {
                                  // 单个子游戏
                                  return (
                                    <Form.Item
                                      label="选择子游戏"
                                      name={[fieldPath, 'jumpGameCode']}
                                      rules={[{ required: true, message: '请选择子游戏' }]}
                                    >
                                      <Select placeholder="请选择子游戏" showSearch>
                                        {games.map(game => (
                                          <Option key={game.code} value={game.code}>
                                            {game.name} ({game.code})
                                          </Option>
                                        ))}
                                      </Select>
                                    </Form.Item>
                                  );
                                } else if (gameCount === 2) {
                                  // 多个子游戏
                                  return (
                                    <Form.Item
                                      label="选择子游戏"
                                      name={[fieldPath, 'jumpGameCodes']}
                                      rules={[
                                        { required: true, message: '请至少选择一个子游戏' },
                                        { type: 'array', min: 2, message: '至少选择两个子游戏' },
                                      ]}
                                      extra="选择多个子游戏，随机跳转"
                                    >
                                      <Select mode="multiple" placeholder="请选择子游戏" showSearch>
                                        {games.map(game => (
                                          <Option key={game.code} value={game.code}>
                                            {game.name} ({game.code})
                                          </Option>
                                        ))}
                                      </Select>
                                    </Form.Item>
                                  );
                                }
                                return null;
                              }}
                            </Form.Item>
                          </Col>
                        </Row>
                      );
                    }
                    return null;
                  }}
                </Form.Item>
              </>
            );
          }
          return null;
        }}
      </Form.Item>
    </>
  );

  const renderGuideTemplate = () => {
    const rewardType = guideRewardType || 'cash';
    return (
      <>

      <Divider orientation="left">奖励配置</Divider>
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label="奖励类型" name="guideRewardType" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'cash', label: '真金' },
                { value: 'coupon', label: '直播游戏券' },
                { value: 'egameCoupon', label: '电子游戏券' },
              ]}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="派奖方式" name={['guide', 'payoutMethod']} rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'auto', label: '自动派发' },
                { value: 'manual', label: '手动领取' },
              ]}
            />
          </Form.Item>
        </Col>
      </Row>

      {rewardType === 'coupon' && renderCouponConfig('guideCoupon', guidePayoutMethod !== 'auto', '直播游戏券')}
      {rewardType === 'egameCoupon' && renderCouponConfig('guideEgameCoupon', guidePayoutMethod !== 'auto', '电子游戏券')}

      <Divider orientation="left">任务配置</Divider>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {GUIDE_TASKS.map((task) => (
          <Card key={task.key} size="small" title={`${task.title}`}>
            <Row gutter={16}>
              <Col span={10}>
                <Form.Item
                  label="完成任务条件"
                  name={['guideTasks', task.key, 'condition']}
                  rules={[{ required: true }]}
                >
                  <Input placeholder={task.condition} suffix={task.unit} />
                </Form.Item>
              </Col>
              <Col span={7}>
                <Form.Item
                  label="每日奖励次数"
                  name={['guideTasks', task.key, 'dailyTimes']}
                  rules={[{ required: true }]}
                >
                  <Space>
                    <InputNumber min={1} style={{ width: 120 }} />
                    <span>次</span>
                  </Space>
                </Form.Item>
              </Col>
              <Col span={7}>
                <Form.Item
                  label={rewardType === 'cash' ? '新人奖励金额' : rewardType === 'coupon' ? '奖励直播游戏券张数' : '奖励电子游戏券张数'}
                  name={rewardType === 'cash' ? ['guideTasks', task.key, 'rewardAmount'] : rewardType === 'coupon' ? ['guideTasks', task.key, 'rewardCouponCount'] : ['guideTasks', task.key, 'rewardEgameCouponCount']}
                  rules={[{ required: true }]}
                >
                  <Space>
                    <InputNumber
                      min={rewardType === 'cash' ? 1 : 1}
                      style={{ width: 140 }}
                    />
                    <span>{rewardType === 'cash' ? 'VND' : '张'}</span>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Card>
        ))}
      </Space>

    </>
  );
  };

  const renderTemplateFields = () => {
    if (templateKey === 'guide') {
      return renderGuideTemplate();
    }
    if (templateKey === 'wheel') {
      return renderWheelTemplate();
    }
    if (templateKey === 'redRain') {
      return renderRedRainTemplate();
    }
    if (templateKey === 'signIn') {
      return renderSignInTemplate();
    }
    if (templateKey === 'onlineDuration') {
      return renderOnlineDurationTemplate();
    }
    if (templateKey === 'downloadApp') {
      return renderDownloadAppTemplate();
    }
    if (templateKey === 'egameReward') {
      return renderEgameRewardTemplate();
    }
    const config = ACTIVITY_TEMPLATES[templateKey];
    return (
      <>
        <Divider orientation="left">{config.label}</Divider>
        <Row gutter={16}>
          {config.fields.map(field => (
            <Col span={12} key={field.key}>
              <Form.Item
                label={field.label}
                name={['templateValues', field.key]}
                rules={[{ required: true, message: `请输入${field.label}` }]}
              >
                {field.component === 'textarea' && (
                  <Input.TextArea placeholder={field.placeholder} rows={3} />
                )}
                {field.component === 'number' && (
                  <InputNumber style={{ width: '100%' }} placeholder={field.placeholder || `请输入${field.label}`} />
                )}
                {field.component === 'datetime' && (
                  <DatePicker showTime style={{ width: '100%' }} />
                )}
                {!field.component && (
                  <Input placeholder={field.placeholder || `请输入${field.label}`} />
                )}
              </Form.Item>
            </Col>
          ))}
        </Row>
      </>
    );
  };

  return (
    <div style={{ padding: 24 }}>
      <Space align="center" style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          活动配置
        </Typography.Title>
        {toolbarButtons}
      </Space>

      <Card style={{ marginBottom: 16 }}>
        <Form layout="inline" form={form} initialValues={filters}>
          <Form.Item name="keyword" label="标题">
            <Input placeholder="请输入标题" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select style={{ width: 160 }} allowClear>
              <Option value="all">全部</Option>
              <Option value="open">开启</Option>
              <Option value="closed">关闭</Option>
            </Select>
          </Form.Item>
          <Form.Item name="module" label="活动模块">
            <Select style={{ width: 160 }} allowClear>
              {MODULE_OPTIONS.map(option => (
                <Option key={option} value={option}>
                  {option}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="type" label="活动类型">
            <Select style={{ width: 180 }} allowClear>
              {TYPE_OPTIONS.map(option => (
                <Option key={option} value={option}>
                  {option}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="userScope" label="用户范围">
            <Select style={{ width: 160 }} allowClear>
              {USER_SCOPE_OPTIONS.map(option => (
                <Option key={option} value={option}>
                  {option}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                搜索
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                重置
              </Button>
              <Button type="dashed" icon={<PlusOutlined />} onClick={() => openDrawer()}>
                新增
              </Button>
              <Button 
                danger 
                icon={<SettingOutlined />} 
                onClick={() => setCategoryModalVisible(true)}
              >
                活动大类配置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card bodyStyle={{ padding: 0 }}>
        <Table
          rowKey="id"
          dataSource={filteredData}
          columns={columns}
          pagination={{ pageSize: 50, showSizeChanger: false, showTotal: total => `共 ${total} 条` }}
          scroll={{ x: 1500 }}
          bordered={false}
        />
      </Card>

      <Drawer
        title={editingRecord ? '编辑活动' : '新增活动'}
        width={900}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        extra={
          <Space>
            <Button onClick={() => setDrawerVisible(false)}>取消</Button>
            <Button type="primary" icon={<InteractionOutlined />} onClick={handleDrawerSubmit}>
              保存
            </Button>
          </Space>
        }
      >
        <Form layout="vertical" form={editorForm}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="活动大类" name="category" rules={[{ required: true, message: '请选择活动大类' }]}>
                <Select placeholder="请选择活动大类" showSearch optionFilterProp="children">
                  {getActivityCategoryOptions(categories).map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="活动模块" name="module" rules={[{ required: true }]}>
                <Select>
                  {MODULE_OPTIONS.map(option => (
                    <Option key={option} value={option}>
                      {option}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="活动类型" name="type" rules={[{ required: true }]}>
                <Select
                  onChange={(val: string) => {
                    const template = inferTemplateKey(val);
                    setTemplateKey(template);
                    editorForm.setFieldValue('template', template);
                  }}
                >
                  {TYPE_OPTIONS.map(option => (
                    <Option key={option} value={option}>
                      {option}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="跳转类型" name="jumpType">
                <Input placeholder="例如：轮盘 / 转盘" />
              </Form.Item>
            </Col>
          </Row>


          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="用户范围" name="userScope" rules={[{ required: true }]}>
                <Select>
                  {USER_SCOPE_OPTIONS.map(option => (
                    <Option key={option} value={option}>
                      {option}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="状态" name="status" rules={[{ required: true }]}>
                <Radio.Group>
                  <Radio value="open">开启</Radio>
                  <Radio value="closed">关闭</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">标签配置</Divider>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item 
                label="可见标签（可选）" 
                name="visibleTags"
                extra="选择指定标签后，只有带上这些标签的用户才能看到这个活动。不选择则所有用户可见。"
              >
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="请选择可见标签（可选，不选则全部可见）"
                  showSearch
                  optionLabelProp="label"
                  filterOption={(input, option) =>
                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {MOCK_TAGS.map(tag => (
                    <Option key={tag.id} value={tag.id} label={tag.name}>
                      <Tag color={tag.color}>{tag.name}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item 
                label="可领奖标签（可选）" 
                name="rewardTags"
                extra="选择指定标签后，只有带上这些标签的用户才能领取奖励。不选择则所有用户可领奖。如果用户没有这些标签，可以看到活动但领取时会提示需要满足条件。"
              >
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="请选择可领奖标签（可选，不选则全部可领奖）"
                  showSearch
                  optionLabelProp="label"
                  filterOption={(input, option) =>
                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {MOCK_TAGS.map(tag => (
                    <Option key={tag.id} value={tag.id} label={tag.name}>
                      <Tag color={tag.color}>{tag.name}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Alert
            message="标签配置说明"
            description={
              <div>
                <p><strong>可见标签：</strong>控制哪些用户可以看到这个活动。不选择则所有用户可见。</p>
                <p><strong>可领奖标签：</strong>控制哪些用户可以领取奖励。如果用户没有可领奖标签，可以看到活动但领取时会提示需要满足条件（例如：需要绑定银行卡）。</p>
                <p><strong>示例：</strong>签到任务设置可见标签为"全部"，可领奖标签为"绑定银行卡"。则所有用户可以看到签到任务，但只有绑定银行卡的用户可以领取，未绑定的用户会提示"需要绑定银行卡"。</p>
              </div>
            }
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />

          <Divider orientation="left">活动领取条件</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                label="同IP奖励数量" 
                name={['claimConditions', 'sameIpRewardCount']}
                extra="同一个IP，有多少个用户使用对应IP领取奖励。超过此数量后，该IP下的用户将无法领取奖励。"
              >
                <InputNumber 
                  min={0} 
                  placeholder="请输入同IP奖励数量（0表示不限制）" 
                  style={{ width: '100%' }} 
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                label="同设备奖励数量" 
                name={['claimConditions', 'sameDeviceRewardCount']}
                extra="同一个设备，有多少个用户使用对应设备领取奖励。超过此数量后，该设备下的用户将无法领取奖励。"
              >
                <InputNumber 
                  min={0} 
                  placeholder="请输入同设备奖励数量（0表示不限制）" 
                  style={{ width: '100%' }} 
                />
              </Form.Item>
            </Col>
          </Row>
          <Alert
            message="领取条件说明"
            description={
              <div>
                <p><strong>同IP奖励数量：</strong>限制同一个IP地址下可以领取奖励的用户数量。例如设置为3，则同一个IP最多只能有3个用户领取该活动的奖励。</p>
                <p><strong>同设备奖励数量：</strong>限制同一个设备下可以领取奖励的用户数量。例如设置为2，则同一个设备最多只能有2个用户领取该活动的奖励。</p>
                <p><strong>提示信息：</strong>当用户因IP或设备限制无法领取时，系统会显示在"语言配置"中设置的多语言提示信息。</p>
              </div>
            }
            type="info"
            showIcon
            style={{ marginTop: 16, marginBottom: 16 }}
          />

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="有效期" name="validity" rules={[{ required: true }]}>
                <Radio.Group
                  onChange={(evt) => {
                    if (evt.target.value === 'permanent') {
                      editorForm.setFieldValue('effectiveDate', null);
                    }
                  }}
                >
                  <Radio value="permanent">永久有效</Radio>
                  <Radio value="custom">自定义</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="自定义结束时间"
                name="effectiveDate"
                dependencies={['validity']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (getFieldValue('validity') === 'custom' && !value) {
                        return Promise.reject(new Error('请选择结束时间'));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePicker showTime style={{ width: '100%' }} disabled={editorForm.getFieldValue('validity') !== 'custom'} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="活动模板" name="template" initialValue={templateKey} hidden>
            <Input />
          </Form.Item>

          {renderTemplateFields()}
          {renderLanguageSection()}
        </Form>
      </Drawer>

      {/* 活动大类配置弹窗 */}
      <Modal
        title="活动大类配置"
        open={categoryModalVisible}
        onCancel={() => setCategoryModalVisible(false)}
        width={1000}
        footer={[
          <Button key="close" onClick={() => setCategoryModalVisible(false)}>
            关闭
          </Button>,
          <Button key="add" type="primary" icon={<PlusOutlined />} onClick={() => {
            setEditingCategory(null);
            categoryForm.resetFields();
            categoryForm.setFieldsValue({
              status: 'active',
              sortOrder: categories.length + 1,
            });
            setCategoryEditModalVisible(true);
          }}>
            新增活动大类
          </Button>,
        ]}
      >
        <Table
          rowKey="id"
          dataSource={categories}
          columns={[
            {
              title: '序号',
              dataIndex: 'sortOrder',
              key: 'sortOrder',
              width: 80,
              align: 'center' as const,
              sorter: (a: ActivityCategory, b: ActivityCategory) => a.sortOrder - b.sortOrder,
            },
            {
              title: '图标预览',
              key: 'icon',
              width: 150,
              align: 'center' as const,
              render: (_: any, record: ActivityCategory) => {
                const hasIcons = record.icons?.dark?.selected || record.icons?.light?.selected;
                if (hasIcons) {
                  return (
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                      {record.icons?.dark?.selected && (
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>黑色选中</div>
                          <img 
                            src={record.icons.dark.selected} 
                            alt="黑色选中" 
                            style={{ width: 32, height: 32, objectFit: 'contain' }}
                          />
                        </div>
                      )}
                      {record.icons?.light?.selected && (
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>白色选中</div>
                          <img 
                            src={record.icons.light.selected} 
                            alt="白色选中" 
                            style={{ width: 32, height: 32, objectFit: 'contain', border: '1px solid #ddd' }}
                          />
                        </div>
                      )}
                    </div>
                  );
                }
                // 兼容旧数据
                const icon = record.icon;
                return (
                  <div style={{ fontSize: 24 }}>
                    {icon === 'user' && '👤'}
                    {icon === 'team' && '👥'}
                    {icon === 'video-camera' && '📹'}
                    {icon === 'play-circle' && '▶️'}
                    {icon === 'trophy' && '🏆'}
                    {icon === 'appstore' && '📱'}
                    {icon === 'gift' && '🎁'}
                    {icon === 'fire' && '🔥'}
                    {icon === 'star' && '⭐'}
                    {icon === 'heart' && '❤️'}
                    {!['user', 'team', 'video-camera', 'play-circle', 'trophy', 'appstore', 'gift', 'fire', 'star', 'heart'].includes(icon) && '📦'}
                  </div>
                );
              },
            },
            {
              title: '简体中文',
              dataIndex: ['name', 'zh'],
              key: 'nameZh',
              width: 120,
            },
            {
              title: '繁体中文',
              dataIndex: ['name', 'zhTw'],
              key: 'nameZhTw',
              width: 120,
            },
            {
              title: '英语',
              dataIndex: ['name', 'en'],
              key: 'nameEn',
              width: 150,
            },
            {
              title: '越南语',
              dataIndex: ['name', 'vi'],
              key: 'nameVi',
              width: 150,
            },
            {
              title: '状态',
              dataIndex: 'status',
              key: 'status',
              width: 100,
              align: 'center' as const,
              render: (status: string) => (
                <Tag color={status === 'active' ? 'success' : 'default'}>
                  {status === 'active' ? '启用' : '禁用'}
                </Tag>
              ),
            },
            {
              title: '操作',
              key: 'action',
              width: 100,
              render: (_: any, record: ActivityCategory) => (
                <Button
                  type="link"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setEditingCategory(record);
                    // 设置表单值，包括图标URL转换为fileList格式
                    const formValues: any = { ...record };
                    if (record.icons) {
                      // 将图标URL转换为Upload组件需要的fileList格式
                      if (record.icons.dark?.selected) {
                        formValues.icons = {
                          ...formValues.icons,
                          dark: {
                            ...formValues.icons.dark,
                            selected: record.icons.dark.selected,
                          },
                        };
                      }
                      if (record.icons.dark?.unselected) {
                        formValues.icons = {
                          ...formValues.icons,
                          dark: {
                            ...formValues.icons.dark,
                            unselected: record.icons.dark.unselected,
                          },
                        };
                      }
                      if (record.icons.light?.selected) {
                        formValues.icons = {
                          ...formValues.icons,
                          light: {
                            ...formValues.icons.light,
                            selected: record.icons.light.selected,
                          },
                        };
                      }
                      if (record.icons.light?.unselected) {
                        formValues.icons = {
                          ...formValues.icons,
                          light: {
                            ...formValues.icons.light,
                            unselected: record.icons.light.unselected,
                          },
                        };
                      }
                    }
                    categoryForm.setFieldsValue(formValues);
                    setCategoryEditModalVisible(true);
                  }}
                >
                  编辑
                </Button>
              ),
            },
          ]}
          pagination={false}
          scroll={{ y: 400 }}
        />
      </Modal>

      {/* 活动大类编辑弹窗 */}
      <Modal
        title={editingCategory ? '编辑活动大类' : '新增活动大类'}
        open={categoryEditModalVisible}
        onCancel={() => {
          setCategoryEditModalVisible(false);
          categoryForm.resetFields();
          setEditingCategory(null);
        }}
        onOk={async () => {
          try {
            const values = await categoryForm.validateFields();
            const now = new Date().toISOString().slice(0, 19).replace('T', ' ');

            if (editingCategory) {
              // 编辑
              setCategories(prev =>
                prev.map(item =>
                  item.id === editingCategory.id
                    ? {
                        ...item,
                        ...values,
                        updateTime: now,
                      }
                    : item
                )
              );
              message.success('活动大类已更新');
            } else {
              // 新增
              const newCategory: ActivityCategory = {
                id: Date.now(),
                ...values,
                createTime: now,
                updateTime: now,
              };
              setCategories(prev => [...prev, newCategory]);
              message.success('活动大类已创建');
            }

            setCategoryEditModalVisible(false);
            categoryForm.resetFields();
            setEditingCategory(null);
          } catch (error) {
            console.error('Validation failed:', error);
          }
        }}
        width={1000}
        okText="保存"
        cancelText="取消"
      >
        <Form
          form={categoryForm}
          layout="vertical"
          initialValues={{
            status: 'active',
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="排序"
                name="sortOrder"
                rules={[{ required: true, message: '请输入排序号' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} placeholder="数字越小越靠前" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="状态"
                name="status"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="active">启用</Option>
                  <Option value="inactive">禁用</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">图标配置</Divider>
          <Alert
            message="图标上传说明"
            description="请为黑色皮肤和白色皮肤分别上传选中和非选中状态的图标。建议使用PNG格式，透明背景。"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Row gutter={16}>
            <Col span={12}>
              <Card size="small" title="黑色皮肤" style={{ marginBottom: 16 }}>
                <Form.Item
                  label="选中状态图标"
                  name={['icons', 'dark', 'selected']}
                  rules={[{ required: true, message: '请上传黑色皮肤选中图标' }]}
                  getValueFromEvent={(e) => {
                    if (Array.isArray(e)) {
                      return e;
                    }
                    if (e && e.fileList && e.fileList.length > 0) {
                      const file = e.fileList[0].originFileObj;
                      if (file) {
                        // 创建预览URL（实际项目中应该上传到服务器后返回URL）
                        const url = URL.createObjectURL(file);
                        categoryForm.setFieldValue(['icons', 'dark', 'selected'], url);
                        return url;
                      }
                    }
                    return e && e.fileList;
                  }}
                  valuePropName="fileList"
                  getValueProps={(value) => {
                    if (!value) return { fileList: [] };
                    if (typeof value === 'string') {
                      return {
                        fileList: [{
                          uid: '-1',
                          name: 'image.png',
                          status: 'done',
                          url: value,
                        }],
                      };
                    }
                    return { fileList: [] };
                  }}
                >
                  <Upload
                    listType="picture-card"
                    maxCount={1}
                    beforeUpload={() => false}
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                  >
                    <div>
                      <UploadOutlined />
                      <div style={{ marginTop: 8 }}>上传</div>
                    </div>
                  </Upload>
                </Form.Item>
                <Form.Item
                  label="非选中状态图标"
                  name={['icons', 'dark', 'unselected']}
                  rules={[{ required: true, message: '请上传黑色皮肤非选中图标' }]}
                  getValueFromEvent={(e) => {
                    if (Array.isArray(e)) {
                      return e;
                    }
                    if (e && e.fileList && e.fileList.length > 0) {
                      const file = e.fileList[0].originFileObj;
                      if (file) {
                        const url = URL.createObjectURL(file);
                        categoryForm.setFieldValue(['icons', 'dark', 'unselected'], url);
                        return url;
                      }
                    }
                    return e && e.fileList;
                  }}
                  valuePropName="fileList"
                  getValueProps={(value) => {
                    if (!value) return { fileList: [] };
                    if (typeof value === 'string') {
                      return {
                        fileList: [{
                          uid: '-1',
                          name: 'image.png',
                          status: 'done',
                          url: value,
                        }],
                      };
                    }
                    return { fileList: [] };
                  }}
                >
                  <Upload
                    listType="picture-card"
                    maxCount={1}
                    beforeUpload={() => false}
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                  >
                    <div>
                      <UploadOutlined />
                      <div style={{ marginTop: 8 }}>上传</div>
                    </div>
                  </Upload>
                </Form.Item>
              </Card>
            </Col>
            <Col span={12}>
              <Card size="small" title="白色皮肤" style={{ marginBottom: 16 }}>
                <Form.Item
                  label="选中状态图标"
                  name={['icons', 'light', 'selected']}
                  rules={[{ required: true, message: '请上传白色皮肤选中图标' }]}
                  getValueFromEvent={(e) => {
                    if (Array.isArray(e)) {
                      return e;
                    }
                    if (e && e.fileList && e.fileList.length > 0) {
                      const file = e.fileList[0].originFileObj;
                      if (file) {
                        const url = URL.createObjectURL(file);
                        categoryForm.setFieldValue(['icons', 'light', 'selected'], url);
                        return url;
                      }
                    }
                    return e && e.fileList;
                  }}
                  valuePropName="fileList"
                  getValueProps={(value) => {
                    if (!value) return { fileList: [] };
                    if (typeof value === 'string') {
                      return {
                        fileList: [{
                          uid: '-1',
                          name: 'image.png',
                          status: 'done',
                          url: value,
                        }],
                      };
                    }
                    return { fileList: [] };
                  }}
                >
                  <Upload
                    listType="picture-card"
                    maxCount={1}
                    beforeUpload={() => false}
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                  >
                    <div>
                      <UploadOutlined />
                      <div style={{ marginTop: 8 }}>上传</div>
                    </div>
                  </Upload>
                </Form.Item>
                <Form.Item
                  label="非选中状态图标"
                  name={['icons', 'light', 'unselected']}
                  rules={[{ required: true, message: '请上传白色皮肤非选中图标' }]}
                  getValueFromEvent={(e) => {
                    if (Array.isArray(e)) {
                      return e;
                    }
                    if (e && e.fileList && e.fileList.length > 0) {
                      const file = e.fileList[0].originFileObj;
                      if (file) {
                        const url = URL.createObjectURL(file);
                        categoryForm.setFieldValue(['icons', 'light', 'unselected'], url);
                        return url;
                      }
                    }
                    return e && e.fileList;
                  }}
                  valuePropName="fileList"
                  getValueProps={(value) => {
                    if (!value) return { fileList: [] };
                    if (typeof value === 'string') {
                      return {
                        fileList: [{
                          uid: '-1',
                          name: 'image.png',
                          status: 'done',
                          url: value,
                        }],
                      };
                    }
                    return { fileList: [] };
                  }}
                >
                  <Upload
                    listType="picture-card"
                    maxCount={1}
                    beforeUpload={() => false}
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                  >
                    <div>
                      <UploadOutlined />
                      <div style={{ marginTop: 8 }}>上传</div>
                    </div>
                  </Upload>
                </Form.Item>
              </Card>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="简体中文名称"
                name={['name', 'zh']}
                rules={[{ required: true, message: '请输入简体中文名称' }]}
              >
                <Input placeholder="请输入简体中文名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="繁体中文名称"
                name={['name', 'zhTw']}
                rules={[{ required: true, message: '请输入繁体中文名称' }]}
              >
                <Input placeholder="请输入繁体中文名称" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="英语名称"
                name={['name', 'en']}
                rules={[{ required: true, message: '请输入英语名称' }]}
              >
                <Input placeholder="请输入英语名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="越南语名称"
                name={['name', 'vi']}
                rules={[{ required: true, message: '请输入越南语名称' }]}
              >
                <Input placeholder="请输入越南语名称" />
              </Form.Item>
            </Col>
          </Row>

        </Form>
      </Modal>
    </div>
  );
};

export default LegacyActivityConfig;


